(function() {
    'use strict';
  
    // Address Validator Variables
    let addressData = [];
    let activeField = null; // Track which address field is currently active (for context-based search)
    let isAutoMapping = false; // Flag to prevent infinite loops during auto-mapping
    let addressDataMap = new Map(); // Fast lookup map: compositeKey -> item (for O(1) lookups)
    let postcodeSet = new Set(); // Fast lookup set for postal codes (O(1) validation)
    let unifiedItems = null; // Cached unified items for lazy loading
    let dropdownsPopulated = {
        subdistrict: false,
        district: false,
        province: false,
        postcode: false
    }; // Track which dropdowns have been populated (lazy loading)
    
    /** Above this unified row count, lists render virtually (filter in memory, cap DOM) to avoid mobile OOM. */
    const ADDR_VIRTUAL_THRESHOLD = 500;
    const ADDR_VIRTUAL_MAX_ROWS = 200;
    const ADDR_VIRTUAL_MIN_CHARS = 1;
    const ADDR_VIRTUAL_INPUT_DEBOUNCE_MS = 240;
    
    /** One document listener closes all validator dropdowns (avoids N duplicate listeners from each init). */
    let addressValidatorOutsideClickBound = false;
    
    /** After programmatic focus on a validation error, skip auto-opening dropdown lists briefly (iOS / UX). */
    let suppressAddressValidatorDropdownOpenUntil = 0;
    
    /** Dedupe Proceed handler when both touchend and click fire (WebKit touch). */
    let lastAddressValidatorProceedAt = 0;
    
    function bindAddressValidatorOutsideClickOnce() {
        if (addressValidatorOutsideClickBound) return;
        addressValidatorOutsideClickBound = true;
        document.addEventListener('click', function (e) {
            const listIds = [
                'address-validator-subdistrict-list',
                'address-validator-district-list',
                'address-validator-province-list',
                'address-postal-code-list',
                'address-validator-saved-address-list'
            ];
            listIds.forEach(function (listId) {
                const list = document.getElementById(listId);
                if (!list || list.style.display !== 'block') return;
                const wrap = list.closest('.searchable-dropdown-wrapper') || list.closest('.form-control');
                if (!wrap || !wrap.contains(e.target)) {
                    list.style.display = 'none';
                }
            });
        }, false);
    }
    
    // Get current language from theme's locale
    function getCurrentLanguage() {
        // Check HTML lang attribute (set by theme from request.locale.iso_code)
        const htmlLang = document.documentElement.lang || '';
        const langCode = htmlLang.split('-')[0].toLowerCase(); // Get first part (e.g., 'th' from 'th-TH')
        
        // Return 'en' if English, otherwise default to 'th' for Thai
        return langCode === 'en' ? 'en' : 'th';
    }
    
    let currentLanguage = getCurrentLanguage(); // 'th' for Thai, 'en' for English
    
    // Initialize address validator form (for page)
    function initAddressValidatorForm() {
        const form = document.getElementById('address-validator-form');
        if (!form) {
            // Form not on this page, skip initialization
            return;
        }
        
        // Initialize address dropdown (non-searchable) - do this immediately
        const savedAddressSelect = document.getElementById('address-validator-saved-address');
        if (savedAddressSelect) {
            initAddressDropdown('address-validator-saved-address-search', 'address-validator-saved-address', 'address-validator-saved-address-list');
            populateAddressDropdown();
        }
        
        // Initialize searchable dropdowns (sub-district, district, province, postcode)
        // Order: Sub District → District → Province → Postcode
        // Initialize event handlers immediately, but defer population
        initSearchableDropdown('address-validator-subdistrict-search', 'address-validator-subdistrict-select', 'address-validator-subdistrict-list', 'subdistrict');
        initSearchableDropdown('address-validator-district-search', 'address-validator-district-select', 'address-validator-district-list', 'district');
        initSearchableDropdown('address-validator-province-search', 'address-validator-province-select', 'address-validator-province-list', 'province');
        // Initialize postcode as searchable dropdown (no hidden select needed, just input and list)
        initPostcodeDropdown();
        
        bindAddressValidatorOutsideClickOnce();
        
        // Update current language from theme locale
        currentLanguage = getCurrentLanguage();
        
        // Setup listener for language changes via theme's localization selector
        setupLanguageChangeListener();
        
        // Setup form handlers immediately (needed for validation)
        setupFormHandlers();
        
        // Load address data from API - this will populate dropdowns when ready
        // Use requestIdleCallback if available to defer non-critical work
        if (window.requestIdleCallback) {
            requestIdleCallback(() => {
                loadAddressData();
            }, { timeout: 2000 }); // Fallback after 2 seconds if idle never comes
        } else {
            // Fallback: use setTimeout to defer after initial render
            setTimeout(() => {
                loadAddressData();
            }, 0);
        }
    }
    
    // Load address data from Sheety API with caching
    function loadAddressData() {
        const CACHE_KEY = 'thailand_address_data';
        const CACHE_TIMESTAMP_KEY = 'thailand_address_data_timestamp';
        const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
        
        // Check cache first
        try {
            const cachedData = localStorage.getItem(CACHE_KEY);
            const cacheTimestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);
            const cachedPostcodeSet = localStorage.getItem('thailand_postcode_set');
            const now = Date.now();
            
            if (cachedData && cacheTimestamp && (now - parseInt(cacheTimestamp, 10)) < CACHE_DURATION) {
                // Use cached data
                addressData = JSON.parse(cachedData);
                if (addressData.length > 0) {
                    // Restore postcode Set from cache for fast validation
                    if (cachedPostcodeSet) {
                        try {
                            postcodeSet = new Set(JSON.parse(cachedPostcodeSet));
                        } catch (e) {
                            // Rebuild if cache is corrupted
                            postcodeSet.clear();
                            addressData.forEach(item => {
                                const postcode = String(item.postcode || '').trim();
                                if (postcode && postcode.length === 5) {
                                    postcodeSet.add(postcode);
                                }
                            });
                        }
                    } else {
                        // Rebuild postcode set if not cached
                        postcodeSet.clear();
                        addressData.forEach(item => {
                            const postcode = String(item.postcode || '').trim();
                            if (postcode && postcode.length === 5) {
                                postcodeSet.add(postcode);
                            }
                        });
                    }
                    
                    // Populate all dropdowns with unified dataset
                    populateUnifiedDropdowns();
                    // Auto-select first address on load if dropdown exists
                    autoSelectFirstAddress();
                    return;
                }
            }
        } catch (e) {
            // Cache read failed, continue with fetch
        }
        
        // Fetch fresh data
        fetch('https://api.sheety.co/5530b81b3883ada579057862e944b50e/jaspalThailandAddressDb/thPostcodeMapping')
            .then(response => {
                if (!response.ok) {
                    throw new Error('Network response was not ok: ' + response.status + ' ' + response.statusText);
                }
                return response.json();
            })
            .then(data => {
                addressData = data.thPostcodeMapping || [];
                if (addressData.length === 0) {
                    return;
                }
                
                // Build postcode Set for fast O(1) validation lookups
                postcodeSet.clear();
                addressData.forEach(item => {
                    const postcode = String(item.postcode || '').trim();
                    if (postcode && postcode.length === 5) {
                        postcodeSet.add(postcode);
                    }
                });
                
                // Cache the data
                try {
                    localStorage.setItem(CACHE_KEY, JSON.stringify(addressData));
                    localStorage.setItem(CACHE_TIMESTAMP_KEY, String(Date.now()));
                    // Also cache the postcode set as an array (Set can't be serialized)
                    localStorage.setItem('thailand_postcode_set', JSON.stringify(Array.from(postcodeSet)));
                } catch (e) {
                    // Cache write failed, continue anyway
                }
                
                // Populate all dropdowns with unified dataset
                populateUnifiedDropdowns();
                // Auto-select first address on load if dropdown exists
                autoSelectFirstAddress();
            })
            .catch(error => {
                // Silently handle error
            });
    }
    
    // Refresh dropdown HTML to bold the segment matching the active field
    // Optimized: Uses Map lookup (O(1)); list.children avoids WebKit querySelectorAll on huge lists.
    // Skips work when this list was already rendered for the same field + language (common on reopen).
    function refreshDropdownHTML(list, activeFieldType) {
        if (!list || !activeFieldType) return;
        if (list.dataset.addrVirtual === '1') return;
        const bakeKey = activeFieldType + '|' + currentLanguage;
        if (list.dataset.addrHierarchyKey === bakeKey) {
            return;
        }
        
        const kids = list.children;
        const rowCount = kids.length;
        
        function isRow(el) {
            return el && el.classList && el.classList.contains('searchable-dropdown-item');
        }
        
        function finishBake() {
            list.dataset.addrHierarchyKey = bakeKey;
        }
        
        if (rowCount > 100) {
            let index = 0;
            const batchSize = 80;
            
            function updateBatch() {
                const end = Math.min(index + batchSize, rowCount);
                for (let i = index; i < end; i++) {
                    const item = kids[i];
                    if (!isRow(item)) continue;
                    const compositeKey = item.getAttribute('data-composite-key');
                    if (compositeKey) {
                        const originalItem = addressDataMap.get(compositeKey);
                        if (originalItem) {
                            item.innerHTML = buildHierarchyDisplayHTML(originalItem, activeFieldType);
                        }
                    }
                }
                index = end;
                if (index < rowCount) {
                    requestAnimationFrame(updateBatch);
                } else {
                    finishBake();
                }
            }
            requestAnimationFrame(updateBatch);
        } else {
            for (let i = 0; i < rowCount; i++) {
                const item = kids[i];
                if (!isRow(item)) continue;
                const compositeKey = item.getAttribute('data-composite-key');
                if (compositeKey) {
                    const originalItem = addressDataMap.get(compositeKey);
                    if (originalItem) {
                        item.innerHTML = buildHierarchyDisplayHTML(originalItem, activeFieldType);
                    }
                }
            }
            finishBake();
        }
    }
    
    function usesVirtualAddressLists() {
        return unifiedItems && unifiedItems.length >= ADDR_VIRTUAL_THRESHOLD;
    }
    
    /** Clear list DOM and placeholder selects when rebuilding the unified dataset (frees memory on mobile). */
    function clearAddressValidatorListDomOnly() {
        dropdownsPopulated.subdistrict = false;
        dropdownsPopulated.district = false;
        dropdownsPopulated.province = false;
        dropdownsPopulated.postcode = false;
        const listIds = [
            'address-validator-subdistrict-list',
            'address-validator-district-list',
            'address-validator-province-list',
            'address-postal-code-list'
        ];
        listIds.forEach(function (id) {
            const el = document.getElementById(id);
            if (!el) return;
            el.innerHTML = '';
            delete el.dataset.addrVirtual;
            delete el.dataset.addrHierarchyKey;
        });
        const subSel = document.getElementById('address-validator-subdistrict-select');
        if (subSel) {
            subSel.innerHTML = '<option value="" selected disabled>Select sub-district</option>';
            subSel.removeAttribute('data-selected-composite');
        }
        const distSel = document.getElementById('address-validator-district-select');
        if (distSel) {
            distSel.innerHTML = '<option value="" selected disabled>Select district</option>';
            distSel.removeAttribute('data-selected-composite');
        }
        const provSel = document.getElementById('address-validator-province-select');
        if (provSel) {
            provSel.innerHTML = '<option value="" selected disabled>Select province</option>';
            provSel.removeAttribute('data-selected-composite');
        }
    }
    
    function unifiedItemMatchesNeedle(item, fieldType, needleLower) {
        if (!item || !needleLower) return false;
        if (fieldType === 'subdistrict') {
            const d = (getDisplayName(item, 'subDistrict') || '').toLowerCase();
            const th = String(item.subDistrictTh || '').toLowerCase();
            const en = String(item.subDistrictEn || '').toLowerCase();
            return d.indexOf(needleLower) !== -1 || th.indexOf(needleLower) !== -1 || en.indexOf(needleLower) !== -1;
        }
        if (fieldType === 'district') {
            const d = (getDisplayName(item, 'district') || '').toLowerCase();
            const th = String(item.districtTh || '').toLowerCase();
            const en = String(item.districtEn || '').toLowerCase();
            return d.indexOf(needleLower) !== -1 || th.indexOf(needleLower) !== -1 || en.indexOf(needleLower) !== -1;
        }
        if (fieldType === 'province') {
            const id = String(item.provinceId || '').toLowerCase();
            const d = (getDisplayName(item, 'province') || '').toLowerCase();
            const th = String(item.provinceTh || '').toLowerCase();
            const en = String(item.provinceEn || '').toLowerCase();
            return id.indexOf(needleLower) !== -1 || d.indexOf(needleLower) !== -1 || th.indexOf(needleLower) !== -1 || en.indexOf(needleLower) !== -1;
        }
        if (fieldType === 'postcode') {
            const digits = needleLower.replace(/\D/g, '');
            const pc = String(item.postcode || '').trim();
            if (!pc) return false;
            if (digits.length > 0) return pc.indexOf(digits) !== -1;
            return pc.toLowerCase().indexOf(needleLower) !== -1;
        }
        return false;
    }
    
    function gatherVirtualMatches(fieldType, needleRaw, maxRows) {
        const items = [];
        if (!unifiedItems || !unifiedItems.length || !maxRows) {
            return { items: items, truncated: false };
        }
        const needle = String(needleRaw || '').trim().toLowerCase();
        if (needle.length < ADDR_VIRTUAL_MIN_CHARS) {
            return { items: items, truncated: false };
        }
        for (let i = 0, len = unifiedItems.length; i < len; i++) {
            if (!unifiedItemMatchesNeedle(unifiedItems[i], fieldType, needle)) continue;
            items.push(unifiedItems[i]);
            if (items.length >= maxRows) {
                return { items: items, truncated: true };
            }
        }
        return { items: items, truncated: false };
    }
    
    function appendAddrListHintRow(list, message) {
        const hint = document.createElement('div');
        hint.className = 'searchable-dropdown-hint';
        hint.setAttribute('role', 'note');
        hint.textContent = message;
        list.appendChild(hint);
    }
    
    function renderVirtualAddressFieldList(list, fieldType, needleRaw) {
        if (!list) return;
        list.innerHTML = '';
        delete list.dataset.addrHierarchyKey;
        const needle = String(needleRaw || '').trim();
        if (needle.length < ADDR_VIRTUAL_MIN_CHARS) {
            appendAddrListHintRow(
                list,
                currentLanguage === 'en'
                    ? 'Type at least ' + ADDR_VIRTUAL_MIN_CHARS + ' character(s) to search.'
                    : 'พิมพ์อย่างน้อย ' + ADDR_VIRTUAL_MIN_CHARS + ' ตัวอักษรเพื่อค้นหา'
            );
            return;
        }
        const pack = gatherVirtualMatches(fieldType, needle, ADDR_VIRTUAL_MAX_ROWS);
        const frag = document.createDocumentFragment();
        for (let i = 0; i < pack.items.length; i++) {
            const rowItem = pack.items[i];
            const compositeKey = [
                String(rowItem.subDistrictTh || '').trim(),
                String(rowItem.districtTh || '').trim(),
                String(rowItem.provinceId || '').trim(),
                String(rowItem.postcode || '').trim()
            ].join('|');
            const listItem = document.createElement('div');
            listItem.className = 'searchable-dropdown-item';
            listItem.innerHTML = buildHierarchyDisplayHTML(rowItem, fieldType);
            if (fieldType === 'postcode') {
                listItem.setAttribute('data-postcode', String(rowItem.postcode || ''));
                listItem.setAttribute('data-value', String(rowItem.postcode || ''));
            } else {
                const v =
                    fieldType === 'subdistrict'
                        ? String(rowItem.subDistrictTh || '').trim()
                        : fieldType === 'district'
                          ? String(rowItem.districtTh || '').trim()
                          : String(rowItem.provinceId || '').trim();
                listItem.setAttribute('data-value', v);
            }
            listItem.setAttribute('data-composite-key', compositeKey);
            listItem.setAttribute('data-subdistrict-th', String(rowItem.subDistrictTh || ''));
            listItem.setAttribute('data-district-th', String(rowItem.districtTh || ''));
            listItem.setAttribute('data-province-id', String(rowItem.provinceId || ''));
            listItem.setAttribute('data-postcode', String(rowItem.postcode || ''));
            const filterParts = [
                getDisplayName(rowItem, 'subDistrict'),
                getDisplayName(rowItem, 'district'),
                getDisplayName(rowItem, 'province'),
                String(rowItem.postcode || '')
            ];
            listItem.setAttribute('data-filter-text', filterParts.join(' ').toLowerCase());
            frag.appendChild(listItem);
        }
        list.appendChild(frag);
        if (pack.truncated) {
            appendAddrListHintRow(
                list,
                currentLanguage === 'en'
                    ? 'Showing the first ' + ADDR_VIRTUAL_MAX_ROWS + ' matches. Type more to narrow results.'
                    : 'แสดง ' + ADDR_VIRTUAL_MAX_ROWS + ' รายการแรก พิมพ์เพิ่มเพื่อแคบผลลัพธ์'
            );
        }
        list.dataset.addrHierarchyKey = fieldType + '|' + currentLanguage;
    }
    
    function syncSelectToSingleAddressItem(select, item, fieldType) {
        if (!select || !item) return;
        let placeholderText = 'Select';
        let optValue = '';
        let optLabel = '';
        let thName = '';
        let enName = '';
        if (fieldType === 'subdistrict') {
            placeholderText = 'Select sub-district';
            optValue = String(item.subDistrictTh || '').trim();
            optLabel = getDisplayName(item, 'subDistrict') || optValue;
            thName = String(item.subDistrictTh || '');
            enName = String(item.subDistrictEn || '');
        } else if (fieldType === 'district') {
            placeholderText = 'Select district';
            optValue = String(item.districtTh || '').trim();
            optLabel = getDisplayName(item, 'district') || optValue;
            thName = String(item.districtTh || '');
            enName = String(item.districtEn || '');
        } else if (fieldType === 'province') {
            placeholderText = 'Select province';
            optValue = String(item.provinceId || '').trim();
            optLabel = getDisplayName(item, 'province') || optValue;
            thName = String(item.provinceTh || '');
            enName = String(item.provinceEn || '');
        }
        select.innerHTML = '';
        const ph = document.createElement('option');
        ph.value = '';
        ph.disabled = true;
        ph.textContent = placeholderText;
        const opt = document.createElement('option');
        opt.value = optValue;
        opt.textContent = optLabel;
        opt.selected = true;
        opt.setAttribute('data-th-name', thName);
        opt.setAttribute('data-en-name', enName);
        opt.setAttribute('data-postcode', String(item.postcode || ''));
        opt.setAttribute('data-subdistrict-th', String(item.subDistrictTh || ''));
        opt.setAttribute('data-district-th', String(item.districtTh || ''));
        opt.setAttribute('data-province-id', String(item.provinceId || ''));
        select.appendChild(ph);
        select.appendChild(opt);
    }
    
    /**
     * Set subdistrict/district/province/postcode from one unified row.
     * In virtual list mode hidden selects only hold placeholder until synced — use this after auto-map.
     */
    function setFieldValueFromAddressRow(fieldType, row) {
        if (!row) return;
        if (fieldType === 'postcode') {
            if (!dropdownsPopulated.postcode) {
                ensureDropdownPopulated('postcode');
            }
            const zip = String(row.postcode || '').trim();
            setFieldValue('postcode', zip, zip);
            return;
        }
        const selectIdMap = {
            subdistrict: 'address-validator-subdistrict-select',
            district: 'address-validator-district-select',
            province: 'address-validator-province-select'
        };
        const searchIdMap = {
            subdistrict: 'address-validator-subdistrict-search',
            district: 'address-validator-district-search',
            province: 'address-validator-province-search'
        };
        if (!dropdownsPopulated[fieldType]) {
            ensureDropdownPopulated(fieldType);
        }
        const select = document.getElementById(selectIdMap[fieldType]);
        const searchInput = document.getElementById(searchIdMap[fieldType]);
        if (!select) return;
        const selectVal =
            fieldType === 'subdistrict'
                ? String(row.subDistrictTh || '').trim()
                : fieldType === 'district'
                  ? String(row.districtTh || '').trim()
                  : String(row.provinceId || '').trim();
        const displayName =
            fieldType === 'subdistrict'
                ? getDisplayName(row, 'subDistrict')
                : fieldType === 'district'
                  ? getDisplayName(row, 'district')
                  : getDisplayName(row, 'province');
        if (usesVirtualAddressLists()) {
            syncSelectToSingleAddressItem(select, row, fieldType);
            const compositeKey = [
                String(row.subDistrictTh || '').trim(),
                String(row.districtTh || '').trim(),
                String(row.provinceId || '').trim(),
                String(row.postcode || '').trim()
            ].join('|');
            select.setAttribute('data-selected-composite', compositeKey);
            if (searchInput) {
                searchInput.value = displayName || selectVal;
                searchInput.removeAttribute('readonly');
                searchInput.disabled = false;
            }
        } else {
            setFieldValue(fieldType, selectVal, displayName || selectVal);
        }
    }
    
    // Populate all dropdowns with the same unified dataset
    function populateUnifiedDropdowns() {
        if (!addressData.length) return;
        
        clearAddressValidatorListDomOnly();
        unifiedItems = null;
        
        // Get all unique combinations: Sub District | District | Province | Postcode
        // Store in global Map for fast O(1) lookups
        addressDataMap.clear();
        const unifiedMap = new Map();
        
        // Pre-compile regex for better performance (numeric check)
        const numericRegex = /^[0-9]+$/;
        
        // For very large datasets (> 10,000 items), process in chunks to avoid blocking
        const CHUNK_SIZE = 5000;
        const totalItems = addressData.length;
        
        if (totalItems > CHUNK_SIZE) {
            // Process in chunks using requestIdleCallback
            let index = 0;
            
            function processChunk() {
                const end = Math.min(index + CHUNK_SIZE, totalItems);
                
                for (let i = index; i < end; i++) {
                    const item = addressData[i];
                    const subdistrictKey = item.subDistrictTh;
                    const subdistrictKeyStr = String(subdistrictKey || '').trim();
                    
                    // Skip invalid subdistrict entries (empty or numeric only)
                    if (!subdistrictKeyStr || numericRegex.test(subdistrictKeyStr)) {
                        continue;
                    }

                    // Build composite key for full hierarchy (optimized string building)
                    const compositeKey = [
                        subdistrictKeyStr,
                        String(item.districtTh || '').trim(),
                        String(item.provinceId || '').trim(),
                        String(item.postcode || '').trim()
                    ].join('|');

                    // Deduplicate by full hierarchy combination
                    if (compositeKey && !unifiedMap.has(compositeKey)) {
                        unifiedMap.set(compositeKey, item);
                        // Store in global lookup map for fast access
                        addressDataMap.set(compositeKey, item);
                    }
                }
                
                index = end;
                
                if (index < totalItems) {
                    // Continue processing next chunk
                    if (window.requestIdleCallback) {
                        requestIdleCallback(processChunk, { timeout: 100 });
                    } else {
                        setTimeout(processChunk, 0);
                    }
                } else {
                    // All chunks processed, finalize
                    unifiedItems = Array.from(unifiedMap.values());
                }
            }
            
            // Start processing
            if (window.requestIdleCallback) {
                requestIdleCallback(processChunk, { timeout: 100 });
            } else {
                setTimeout(processChunk, 0);
            }
        } else {
            // For smaller datasets, process all at once
            for (let i = 0; i < addressData.length; i++) {
                const item = addressData[i];
                const subdistrictKey = item.subDistrictTh;
                const subdistrictKeyStr = String(subdistrictKey || '').trim();
                
                // Skip invalid subdistrict entries (empty or numeric only)
                if (!subdistrictKeyStr || numericRegex.test(subdistrictKeyStr)) {
                    continue;
                }

                // Build composite key for full hierarchy (optimized string building)
                const compositeKey = [
                    subdistrictKeyStr,
                    String(item.districtTh || '').trim(),
                    String(item.provinceId || '').trim(),
                    String(item.postcode || '').trim()
                ].join('|');

                // Deduplicate by full hierarchy combination
                if (compositeKey && !unifiedMap.has(compositeKey)) {
                    unifiedMap.set(compositeKey, item);
                    // Store in global lookup map for fast access
                    addressDataMap.set(compositeKey, item);
                }
            }
            
            // Get all unique items in original order and cache for lazy loading
            unifiedItems = Array.from(unifiedMap.values());
        }
        
        // LAZY LOADING: Don't populate dropdowns immediately
        // Dropdowns will be populated on-demand when first opened
        // This significantly improves initial load time for large datasets
        
        // For very small datasets (< 100 items), populate immediately for better UX
        if (unifiedItems && unifiedItems.length < 100) {
            populateSubdistrictDropdown(unifiedItems);
            populateDistrictDropdown(unifiedItems);
            populateProvinceDropdownUnified(unifiedItems);
            populatePostcodeDropdownUnified(unifiedItems);
            // Mark all as populated
            dropdownsPopulated.subdistrict = true;
            dropdownsPopulated.district = true;
            dropdownsPopulated.province = true;
            dropdownsPopulated.postcode = true;
        }
        // For larger datasets, dropdowns will populate lazily on first open
    }
    
    // Lazy populate a dropdown if not already populated
    function ensureDropdownPopulated(fieldType) {
        // Wait for unifiedItems to be ready (in case data is still processing)
        if (!unifiedItems) {
            // If data is still processing, wait a bit and try again
            setTimeout(() => ensureDropdownPopulated(fieldType), 50);
            return;
        }
        
        if (unifiedItems.length === 0) return;
        
        // Check if already populated
        if (dropdownsPopulated[fieldType]) return;
        
        // Populate based on field type
        // For large datasets, use requestIdleCallback to avoid blocking
        const populate = () => {
            switch (fieldType) {
                case 'subdistrict':
                    populateSubdistrictDropdown(unifiedItems);
                    dropdownsPopulated.subdistrict = true;
                    break;
                case 'district':
                    populateDistrictDropdown(unifiedItems);
                    dropdownsPopulated.district = true;
                    break;
                case 'province':
                    populateProvinceDropdownUnified(unifiedItems);
                    dropdownsPopulated.province = true;
                    break;
                case 'postcode':
                    populatePostcodeDropdownUnified(unifiedItems);
                    dropdownsPopulated.postcode = true;
                    break;
            }
        };
        
        // Virtual mode: only tags lists / resets selects — no giant DOM build; run synchronously.
        if (usesVirtualAddressLists()) {
            populate();
            return;
        }
        
        // For very large full-DOM datasets, defer population slightly to keep UI responsive
        if (unifiedItems.length > 5000 && window.requestIdleCallback) {
            requestIdleCallback(populate, { timeout: 200 });
        } else {
            // For smaller datasets, populate immediately
            populate();
        }
    }
    
    // Populate subdistrict dropdown with unified dataset (optimized with DocumentFragment)
    function populateSubdistrictDropdown(unifiedItems) {
        const subdistrictSelect = document.getElementById('address-validator-subdistrict-select');
        const subdistrictList = document.getElementById('address-validator-subdistrict-list');
        if (!subdistrictSelect) return;
        
        subdistrictSelect.innerHTML = '<option value="" selected disabled>Select sub-district</option>';
        subdistrictSelect.removeAttribute('data-selected-composite');
        if (subdistrictList) {
            subdistrictList.innerHTML = '';
            subdistrictList.removeAttribute('data-addr-hierarchy-key');
        }
        
        if (usesVirtualAddressLists()) {
            if (subdistrictList) {
                subdistrictList.dataset.addrVirtual = '1';
                renderVirtualAddressFieldList(subdistrictList, 'subdistrict', '');
            }
            const subdistrictSearchInput = document.getElementById('address-validator-subdistrict-search');
            if (subdistrictSearchInput) {
                subdistrictSearchInput.disabled = false;
                subdistrictSearchInput.removeAttribute('readonly');
            }
            return;
        }
        
        // Use DocumentFragment to batch DOM updates (reduces reflows)
        const selectFragment = document.createDocumentFragment();
        const listFragment = document.createDocumentFragment();
        
        unifiedItems.forEach(item => {
            const subdistrictValue = item.subDistrictTh || '';
            const displayName = getDisplayName(item, 'subDistrict');
            
            if (!subdistrictValue || !displayName) return;
            
            // Build composite key for efficient lookup
            const compositeKey = [
                String(item.subDistrictTh || '').trim(),
                String(item.districtTh || '').trim(),
                String(item.provinceId || '').trim(),
                String(item.postcode || '').trim()
            ].join('|');
            
            // Add to hidden select
            const option = document.createElement('option');
            option.value = subdistrictValue;
            option.textContent = displayName;
            option.setAttribute('data-th-name', item.subDistrictTh || '');
            option.setAttribute('data-en-name', item.subDistrictEn || '');
            option.setAttribute('data-postcode', String(item.postcode || ''));
            option.setAttribute('data-district-th', String(item.districtTh || ''));
            option.setAttribute('data-province-id', String(item.provinceId || ''));
            selectFragment.appendChild(option);
            
            // Add to searchable list
            if (subdistrictList) {
                const listItem = document.createElement('div');
                listItem.className = 'searchable-dropdown-item';
                listItem.innerHTML = buildHierarchyDisplayHTML(item, 'subdistrict');
                listItem.setAttribute('data-value', subdistrictValue);
                listItem.setAttribute('data-composite-key', compositeKey);
                listItem.setAttribute('data-subdistrict-th', String(item.subDistrictTh || ''));
                listItem.setAttribute('data-district-th', String(item.districtTh || ''));
                listItem.setAttribute('data-province-id', String(item.provinceId || ''));
                listItem.setAttribute('data-postcode', String(item.postcode || ''));
                listItem.setAttribute('data-filter-text', (displayName || '').toLowerCase());
                listFragment.appendChild(listItem);
            }
        });
        
        // Batch append to DOM (single reflow)
        subdistrictSelect.appendChild(selectFragment);
        if (subdistrictList) {
            subdistrictList.appendChild(listFragment);
            subdistrictList.dataset.addrHierarchyKey = 'subdistrict|' + currentLanguage;
            delete subdistrictList.dataset.addrVirtual;
        }
        
        // Enable subdistrict input
        const subdistrictSearchInput = document.getElementById('address-validator-subdistrict-search');
        if (subdistrictSearchInput) {
            subdistrictSearchInput.disabled = false;
            subdistrictSearchInput.removeAttribute('readonly');
        }
    }
    
    // Populate district dropdown with unified dataset (optimized with DocumentFragment)
    function populateDistrictDropdown(unifiedItems) {
        const districtSelect = document.getElementById('address-validator-district-select');
        const districtList = document.getElementById('address-validator-district-list');
        if (!districtSelect) return;
        
        districtSelect.innerHTML = '<option value="" selected disabled>Select district</option>';
        districtSelect.removeAttribute('data-selected-composite');
        if (districtList) {
            districtList.innerHTML = '';
            districtList.removeAttribute('data-addr-hierarchy-key');
        }
        
        if (usesVirtualAddressLists()) {
            if (districtList) {
                districtList.dataset.addrVirtual = '1';
                renderVirtualAddressFieldList(districtList, 'district', '');
            }
            const districtSearchInput = document.getElementById('address-validator-district-search');
            if (districtSearchInput) {
                districtSearchInput.disabled = false;
                districtSearchInput.removeAttribute('readonly');
            }
            return;
        }
        
        // Use DocumentFragment to batch DOM updates (reduces reflows)
        const selectFragment = document.createDocumentFragment();
        const listFragment = document.createDocumentFragment();
        
        unifiedItems.forEach(item => {
            const districtValue = item.districtTh || '';
            const displayName = getDisplayName(item, 'district');
            
            if (!districtValue || !displayName) return;
            
            // Build composite key for efficient lookup
            const compositeKey = [
                String(item.subDistrictTh || '').trim(),
                String(item.districtTh || '').trim(),
                String(item.provinceId || '').trim(),
                String(item.postcode || '').trim()
            ].join('|');
            
            // Add to hidden select
            const option = document.createElement('option');
            option.value = districtValue;
            option.textContent = displayName;
            option.setAttribute('data-th-name', item.districtTh || '');
            option.setAttribute('data-en-name', item.districtEn || '');
            option.setAttribute('data-province-id', String(item.provinceId || ''));
            option.setAttribute('data-postcode', String(item.postcode || ''));
            option.setAttribute('data-subdistrict-th', String(item.subDistrictTh || ''));
            selectFragment.appendChild(option);
            
            // Add to searchable list
            if (districtList) {
                const listItem = document.createElement('div');
                listItem.className = 'searchable-dropdown-item';
                listItem.innerHTML = buildHierarchyDisplayHTML(item, 'district');
                listItem.setAttribute('data-value', districtValue);
                listItem.setAttribute('data-composite-key', compositeKey);
                listItem.setAttribute('data-subdistrict-th', String(item.subDistrictTh || ''));
                listItem.setAttribute('data-province-id', String(item.provinceId || ''));
                listItem.setAttribute('data-postcode', String(item.postcode || ''));
                listItem.setAttribute('data-filter-text', (displayName || '').toLowerCase());
                listFragment.appendChild(listItem);
            }
        });
        
        // Batch append to DOM (single reflow)
        districtSelect.appendChild(selectFragment);
        if (districtList) {
            districtList.appendChild(listFragment);
            districtList.dataset.addrHierarchyKey = 'district|' + currentLanguage;
            delete districtList.dataset.addrVirtual;
        }
        
        // Enable district input
        const districtSearchInput = document.getElementById('address-validator-district-search');
        if (districtSearchInput) {
            districtSearchInput.disabled = false;
            districtSearchInput.removeAttribute('readonly');
        }
    }
    
    // Populate province dropdown with unified dataset (optimized with DocumentFragment)
    function populateProvinceDropdownUnified(unifiedItems) {
        const provinceSelect = document.getElementById('address-validator-province-select');
        const provinceList = document.getElementById('address-validator-province-list');
        if (!provinceSelect) return;
        
        provinceSelect.innerHTML = '<option value="" selected disabled>Select province</option>';
        provinceSelect.removeAttribute('data-selected-composite');
        if (provinceList) {
            provinceList.innerHTML = '';
            provinceList.removeAttribute('data-addr-hierarchy-key');
        }
        
        if (usesVirtualAddressLists()) {
            if (provinceList) {
                provinceList.dataset.addrVirtual = '1';
                renderVirtualAddressFieldList(provinceList, 'province', '');
            }
            return;
        }
        
        // Use DocumentFragment to batch DOM updates (reduces reflows)
        const selectFragment = document.createDocumentFragment();
        const listFragment = document.createDocumentFragment();
        
        unifiedItems.forEach(item => {
            const provinceValue = String(item.provinceId || '');
            const displayName = getDisplayName(item, 'province');
            
            if (!provinceValue || !displayName) return;
            
            // Build composite key for efficient lookup
            const compositeKey = [
                String(item.subDistrictTh || '').trim(),
                String(item.districtTh || '').trim(),
                String(item.provinceId || '').trim(),
                String(item.postcode || '').trim()
            ].join('|');
            
            // Add to hidden select
            const option = document.createElement('option');
            option.value = provinceValue;
            option.textContent = displayName;
            option.setAttribute('data-th-name', item.provinceTh || '');
            option.setAttribute('data-en-name', item.provinceEn || '');
            option.setAttribute('data-postcode', String(item.postcode || ''));
            option.setAttribute('data-subdistrict-th', String(item.subDistrictTh || ''));
            option.setAttribute('data-district-th', String(item.districtTh || ''));
            selectFragment.appendChild(option);
            
            // Add to searchable list
            if (provinceList) {
                const listItem = document.createElement('div');
                listItem.className = 'searchable-dropdown-item';
                listItem.innerHTML = buildHierarchyDisplayHTML(item, 'province');
                listItem.setAttribute('data-value', provinceValue);
                listItem.setAttribute('data-composite-key', compositeKey);
                listItem.setAttribute('data-subdistrict-th', String(item.subDistrictTh || ''));
                listItem.setAttribute('data-district-th', String(item.districtTh || ''));
                listItem.setAttribute('data-postcode', String(item.postcode || ''));
                listItem.setAttribute('data-filter-text', (displayName || '').toLowerCase());
                listFragment.appendChild(listItem);
            }
        });
        
        // Batch append to DOM (single reflow)
        provinceSelect.appendChild(selectFragment);
        if (provinceList) {
            provinceList.appendChild(listFragment);
            provinceList.dataset.addrHierarchyKey = 'province|' + currentLanguage;
            delete provinceList.dataset.addrVirtual;
        }
    }
    
    // Populate postcode dropdown with unified dataset (optimized with DocumentFragment)
    function populatePostcodeDropdownUnified(unifiedItems) {
        const postcodeList = document.getElementById('address-postal-code-list');
        if (!postcodeList) return;
        
        postcodeList.innerHTML = '';
        postcodeList.removeAttribute('data-addr-hierarchy-key');
        
        if (usesVirtualAddressLists()) {
            postcodeList.dataset.addrVirtual = '1';
            renderVirtualAddressFieldList(postcodeList, 'postcode', '');
            return;
        }
        
        // Use DocumentFragment to batch DOM updates (reduces reflows)
        const listFragment = document.createDocumentFragment();
        
        unifiedItems.forEach(item => {
            const postcode = String(item.postcode || '');
            
            if (!postcode || postcode.length !== 5) return;
            
            // Build composite key for efficient lookup
            const compositeKey = [
                String(item.subDistrictTh || '').trim(),
                String(item.districtTh || '').trim(),
                String(item.provinceId || '').trim(),
                String(item.postcode || '').trim()
            ].join('|');
            
            // Add to searchable list
            const listItem = document.createElement('div');
            listItem.className = 'searchable-dropdown-item';
            listItem.innerHTML = buildHierarchyDisplayHTML(item, 'postcode');
            listItem.setAttribute('data-postcode', postcode);
            listItem.setAttribute('data-composite-key', compositeKey);
            listItem.setAttribute('data-subdistrict-th', String(item.subDistrictTh || ''));
            listItem.setAttribute('data-district-th', String(item.districtTh || ''));
            listItem.setAttribute('data-province-id', String(item.provinceId || ''));
            listItem.setAttribute('data-filter-text', postcode.toLowerCase());
            listFragment.appendChild(listItem);
        });
        
        // Batch append to DOM (single reflow)
        postcodeList.appendChild(listFragment);
        postcodeList.dataset.addrHierarchyKey = 'postcode|' + currentLanguage;
        delete postcodeList.dataset.addrVirtual;
    }
    
    // Initialize postcode dropdown (similar to other searchable dropdowns)
    function initPostcodeDropdown() {
        const postalCodeInput = findPostalCodeInput();
        const postcodeList = document.getElementById('address-postal-code-list');
        
        if (!postalCodeInput || !postcodeList) return;
        
        postcodeList.style.touchAction = 'manipulation';
        
        // Set maxlength attribute
        postalCodeInput.setAttribute('maxlength', '5');
        postalCodeInput.setAttribute('inputmode', 'numeric');
        postalCodeInput.setAttribute('pattern', '[0-9]*');
        
        // Track if dropdown should stay open
        let keepDropdownOpen = false;
        let postcodeFilterRaf = null;
        let postcodeFilterDebounce = null;
        let lastPostcodeListPickAt = 0;
        const wrapper = postalCodeInput.closest('.searchable-dropdown-wrapper') || postalCodeInput.closest('.form-control');
        
        // Function to close dropdown
        function closeDropdown() {
            keepDropdownOpen = false;
            postcodeList.style.display = 'none';
        }
        
        // Function to open dropdown
        function openDropdown() {
            if (Date.now() < suppressAddressValidatorDropdownOpenUntil) {
                list.style.display = 'none';
                return;
            }
            // LAZY LOADING: Populate dropdown if not already populated
            if (!dropdownsPopulated.postcode) {
                ensureDropdownPopulated('postcode');
            }
            
            keepDropdownOpen = true;
            // Close all other dropdowns first
            closeAllDropdowns('address-postal-code-list');
            if (postcodeList.dataset.addrVirtual === '1') {
                const digits = postalCodeInput.value.replace(/[^0-9]/g, '').slice(0, 5);
                renderVirtualAddressFieldList(postcodeList, 'postcode', digits);
                const hasRows = postcodeList.querySelector('.searchable-dropdown-item');
                const hasHint = postcodeList.querySelector('.searchable-dropdown-hint');
                postcodeList.style.display = hasRows || hasHint ? 'block' : 'none';
            } else {
                showAllDropdownItems(postcodeList);
                requestAnimationFrame(() => {
                    refreshDropdownHTML(postcodeList, 'postcode');
                });
            }
            if (document.activeElement !== postalCodeInput && !postalCodeInput.disabled) {
                try {
                    postalCodeInput.focus({ preventScroll: true });
                } catch (err) {
                    postalCodeInput.focus();
                }
            }
        }
        
        // Set active field on focus
        postalCodeInput.addEventListener('focus', function() {
            setActiveField('postcode');
            openDropdown();
        });
        
        // Clear active field on blur
        postalCodeInput.addEventListener('blur', function(e) {
            const relatedTarget = e.relatedTarget;
            if (relatedTarget && wrapper && wrapper.contains(relatedTarget)) {
                keepDropdownOpen = true;
                return;
            }
            
            setTimeout(() => {
                if (!keepDropdownOpen) {
                    closeDropdown();
                    // Validate postcode value
                    const postcodeValue = postalCodeInput.value.trim();
                    if (postcodeValue) {
                        const matchingItem = postcodeList.querySelector(`[data-postcode="${postcodeValue}"]`);
                        const validBySet =
                            postcodeList.dataset.addrVirtual === '1' &&
                            postcodeValue.length === 5 &&
                            postcodeSet.has(postcodeValue);
                        if (matchingItem || validBySet) {
                            // Valid postcode - clear all address field errors (since postcode can trigger auto-mapping)
                            clearAllAddressFieldErrors();
                        } else {
                            // Invalid postcode - show error
                            // Check if it's a valid format first (5 digits)
                            if (postcodeValue.length === 5 && /^[0-9]+$/.test(postcodeValue)) {
                                // Format is valid but doesn't exist in the list
                                showFieldError('address[zip]', 'Please select an item from the list');
                            } else if (postcodeValue.length > 0) {
                                // Invalid format
                                if (postcodeValue.length !== 5) {
                                    showFieldError('address[zip]', 'Postal code must be 5 digits');
                                } else {
                                    showFieldError('address[zip]', 'Postal code must contain only numbers');
                                }
                            }
                        }
                    }
                    if (activeField === 'postcode') {
                        clearActiveField();
                    }
                }
                keepDropdownOpen = false;
            }, 200);
        });
        
        // Only allow numeric input
        postalCodeInput.addEventListener('keypress', function(e) {
            if (!/[0-9]/.test(e.key)) {
                e.preventDefault();
            }
        });
        
        // Handle input - filter + typeahead batched to next frame (reduces lag on large lists)
        postalCodeInput.addEventListener('input', function() {
            if (activeField !== 'postcode') {
                setActiveField('postcode');
            }
            closeAllDropdowns('address-postal-code-list');
            
            let value = this.value.replace(/[^0-9]/g, '').slice(0, 5);
            if (this.value !== value) {
                this.value = value;
            }
            const searchTerm = value.trim();
            if (searchTerm.length > 0) {
                clearFieldError('address[zip]');
            }
            
            if (postcodeList.dataset.addrVirtual === '1') {
                if (postcodeFilterRaf) cancelAnimationFrame(postcodeFilterRaf);
                postcodeFilterRaf = null;
                if (postcodeFilterDebounce) clearTimeout(postcodeFilterDebounce);
                postcodeFilterDebounce = setTimeout(function () {
                    postcodeFilterDebounce = null;
                    if (postcodeList.dataset.addrVirtual !== '1') return;
                    const v = postalCodeInput.value.replace(/[^0-9]/g, '').slice(0, 5).trim();
                    applyDropdownFilterAndHighlight(postcodeList, 'postcode', postalCodeInput, v.toLowerCase());
                }, ADDR_VIRTUAL_INPUT_DEBOUNCE_MS);
            } else {
                if (postcodeFilterRaf) cancelAnimationFrame(postcodeFilterRaf);
                postcodeFilterRaf = requestAnimationFrame(function () {
                    postcodeFilterRaf = null;
                    const v = postalCodeInput.value.replace(/[^0-9]/g, '').slice(0, 5).trim();
                    applyDropdownFilterAndHighlight(postcodeList, 'postcode', postalCodeInput, v.toLowerCase());
                });
            }
        });
        
        const postcodeListScrollPickGuard = createListScrollVsTapGuard();
        
        postcodeList.addEventListener('touchstart', function(e) {
            keepDropdownOpen = true;
            postcodeListScrollPickGuard.touchStart(e);
        }, { passive: true });
        postcodeList.addEventListener('touchmove', function(e) {
            postcodeListScrollPickGuard.touchMove(e);
        }, { passive: true });
        postcodeList.addEventListener('touchcancel', function () {
            postcodeListScrollPickGuard.reset();
        }, { passive: true });
        
        function handlePostcodeListPick(e) {
            const item = e.target.closest('.searchable-dropdown-item');
            if (!item) return;
            
            const postcode = item.getAttribute('data-postcode');
            if (!postcode) return;
            
            const storedData = {
                subdistrictTh: item.getAttribute('data-subdistrict-th'),
                districtTh: item.getAttribute('data-district-th'),
                provinceId: item.getAttribute('data-province-id'),
                postcode: postcode
            };
            
            const compositeKey = item.getAttribute('data-composite-key');
            const addressItem = compositeKey ? addressDataMap.get(compositeKey) : null;
            
            const currentSubdistrict = document.getElementById('address-validator-subdistrict-select')?.value || '';
            const currentDistrict = document.getElementById('address-validator-district-select')?.value || '';
            const currentProvince = document.getElementById('address-validator-province-select')?.value || '';
            const currentPostcode = postalCodeInput.value || '';
            
            const isExactMatch =
                currentPostcode === postcode &&
                currentSubdistrict === storedData.subdistrictTh &&
                currentDistrict === storedData.districtTh &&
                currentProvince === storedData.provinceId;
            
            if (isExactMatch) {
                lastPostcodeListPickAt = Date.now();
                closeDropdown();
                return;
            }
            
            lastPostcodeListPickAt = Date.now();
            
            postalCodeInput.value = postcode;
            clearAllAddressFieldErrors();
            
            if (storedData.provinceId || storedData.districtTh || storedData.subdistrictTh) {
                autoMapRelatedFieldsWithData('postcode', postcode, storedData, addressItem);
            } else {
                autoMapRelatedFields('postcode', postcode);
            }
            
            keepDropdownOpen = false;
            postcodeList.style.display = 'none';
            
            if (activeField === 'postcode') {
                clearActiveField();
            }
        }
        
        postcodeList.addEventListener('touchend', function(e) {
            if (postcodeListScrollPickGuard.consumeIfScrollGesture()) return;
            const item = e.target.closest('.searchable-dropdown-item');
            if (!item) return;
            e.stopPropagation();
            e.preventDefault();
            handlePostcodeListPick(e);
        }, { passive: false });
        
        postcodeList.addEventListener('click', function(e) {
            e.stopPropagation();
            if (postcodeListScrollPickGuard.shouldSuppressSyntheticClick()) {
                e.preventDefault();
                return;
            }
            if (Date.now() - lastPostcodeListPickAt < 450) {
                e.preventDefault();
                return;
            }
            handlePostcodeListPick(e);
        });
        
        // Apply typeahead functionality
        applyTypeaheadToDropdown('address-postal-code', null, 'address-postal-code-list');
    }
    
    // Refresh dropdowns when language changes (called when theme locale changes)
    function refreshDropdownsForLanguageChange() {
        const provinceSelect = document.getElementById('address-validator-province-select');
        const districtSelect = document.getElementById('address-validator-district-select');
        const subdistrictSelect = document.getElementById('address-validator-subdistrict-select');
        
        // Update current language from theme
        const newLanguage = getCurrentLanguage();
        if (newLanguage === currentLanguage) {
            return; // No change
        }
        
        const oldLanguage = currentLanguage;
        currentLanguage = newLanguage;
        
        // Refresh all dropdowns with new language - use unified populate function
        populateUnifiedDropdowns();
        
        // Restore selections if they exist
        if (provinceSelect && provinceSelect.value) {
            const currentProvince = provinceSelect.value;
            if (provinceSelect.querySelector(`option[value="${currentProvince}"]`)) {
                provinceSelect.value = currentProvince;
                updateProvinceSearchInput();
            }
        }
        
        if (districtSelect && districtSelect.value) {
            const currentDistrict = districtSelect.value;
            // Find matching option in the full list
                const districtOption = Array.from(districtSelect.options).find(opt => {
                    const thName = opt.getAttribute('data-th-name');
                    return opt.value === currentDistrict || (thName && thName === currentDistrict);
                });
                if (districtOption) {
                    districtSelect.value = districtOption.value;
                    updateDistrictSearchInput();
            }
        }
        
        if (subdistrictSelect && subdistrictSelect.value) {
            const currentSubdistrict = subdistrictSelect.value;
            // Find matching option in the full list
                const subdistrictOption = Array.from(subdistrictSelect.options).find(opt => {
                    const thName = opt.getAttribute('data-th-name');
                    return opt.value === currentSubdistrict || (thName && thName === currentSubdistrict);
                });
                if (subdistrictOption) {
                    subdistrictSelect.value = subdistrictOption.value;
                    updateSubdistrictSearchInput();
            }
        }
    }
    
    // Monitor for language changes (when theme's localization form is submitted)
    function setupLanguageChangeListener() {
        // Listen for form submissions that might change the locale
        document.addEventListener('submit', function(e) {
            const form = e.target;
            if (form && form.id && form.id.includes('localization-form')) {
                // Language is changing, refresh dropdowns after a short delay
                setTimeout(() => {
                    refreshDropdownsForLanguageChange();
                }, 500);
            }
        });
        
        // Also check periodically for HTML lang attribute changes (fallback)
        let lastLang = document.documentElement.lang;
        setInterval(() => {
            const currentLang = document.documentElement.lang;
            if (currentLang !== lastLang) {
                lastLang = currentLang;
                refreshDropdownsForLanguageChange();
            }
        }, 1000);
    }
    
    // Helper: Build hierarchy display text following sequence: Sub District > District > Province > Postcode
    // Always show the FULL hierarchy for all dropdowns to ensure correct mapping
    // Helper: Build hierarchy display HTML with active field segment bold
    // fieldType determines which segment should be bold
    function buildHierarchyDisplayHTML(item, fieldType) {
        const parts = [];
        const fieldMap = {
            'subdistrict': 'subDistrict',
            'district': 'district',
            'province': 'province',
            'postcode': 'postcode'
        };
        
        // Get all parts
        const subDistrict = getDisplayName(item, 'subDistrict');
        const district = getDisplayName(item, 'district');
        const province = getDisplayName(item, 'province');
        const postcode = String(item.postcode || '');
        
        // Determine which field to bold based on fieldType
        const activeFieldName = fieldMap[fieldType] || '';
        
        // Build parts array with bold formatting for active field
        if (subDistrict) {
            const display = subDistrict;
            if (activeFieldName === 'subDistrict') {
                parts.push(`<strong>${display}</strong>`);
            } else {
                parts.push(display);
            }
        }
        if (district) {
            const display = district;
            if (activeFieldName === 'district') {
                parts.push(`<strong>${display}</strong>`);
            } else {
                parts.push(display);
            }
        }
        if (province) {
            const display = province;
            if (activeFieldName === 'province') {
                parts.push(`<strong>${display}</strong>`);
            } else {
                parts.push(display);
            }
        }
        if (postcode) {
            const display = postcode;
            if (activeFieldName === 'postcode') {
                parts.push(`<strong>${display}</strong>`);
            } else {
                parts.push(display);
            }
        }
        
        // Join with separator
        return parts.length > 0 ? parts.join(' > ') : '';
    }
    
    // Helper to get display name based on current language
    // Standard i18n fallback pattern: Try requested language → Fallback to source language → Return empty
    function getDisplayName(item, field) {
        // Helper to check if a value is valid (not null, undefined, or empty string)
        function isValidValue(val) {
            return val !== null && val !== undefined && String(val).trim() !== '';
        }
        
        // Map field names to actual data property names (matching API structure)
        const fieldMap = {
            'subDistrict': 'subDistrictTh',  // Maps to item.subDistrictTh
            'district': 'districtTh',         // Maps to item.districtTh
            'province': 'provinceTh'          // Maps to item.provinceTh
        };
        
        if (currentLanguage === 'en') {
            const enField = field + 'En';  // e.g., 'subDistrictEn'
            const thFieldName = fieldMap[field] || (field + 'Th');
            const enValue = item[enField];
            const thValue = item[thFieldName];
            
            // Try English first
            if (isValidValue(enValue)) {
                return String(enValue).trim();
            }
            
            // Fallback to Thai if English is not available
            if (isValidValue(thValue)) {
                return String(thValue).trim();
            }
            
            // If both are invalid, return empty string (will be filtered out)
            return '';
        } else {
            // Thai language - use Thai field directly from fieldMap
            const thFieldName = fieldMap[field] || (field + 'Th');
            let thValue = item[thFieldName];
            
            // If fieldMap lookup didn't work, try direct property access
            if (!isValidValue(thValue)) {
                thValue = item[field + 'Th'];
            }
            
            if (isValidValue(thValue)) {
                return String(thValue).trim();
            }
            
            // If Thai is also missing, return empty (shouldn't happen in practice)
            return '';
        }
    }
    
    // Update province search input
    function updateProvinceSearchInput() {
        const provinceSelect = document.getElementById('address-validator-province-select');
        const provinceSearchInput = document.getElementById('address-validator-province-search');
        if (provinceSelect && provinceSearchInput && provinceSelect.value) {
            const selectedOption = provinceSelect.options[provinceSelect.selectedIndex];
            if (selectedOption) {
                provinceSearchInput.value = selectedOption.textContent;
            }
        }
    }
    
    // Update district search input
    function updateDistrictSearchInput() {
        const districtSelect = document.getElementById('address-validator-district-select');
        const districtSearchInput = document.getElementById('address-validator-district-search');
        if (districtSelect && districtSearchInput && districtSelect.value) {
            const selectedOption = districtSelect.options[districtSelect.selectedIndex];
            if (selectedOption) {
                districtSearchInput.value = selectedOption.textContent;
            }
        }
    }
    
    // Update subdistrict search input
    function updateSubdistrictSearchInput() {
        const subdistrictSelect = document.getElementById('address-validator-subdistrict-select');
        const subdistrictSearchInput = document.getElementById('address-validator-subdistrict-search');
        if (subdistrictSelect && subdistrictSearchInput && subdistrictSelect.value) {
            const selectedOption = subdistrictSelect.options[subdistrictSelect.selectedIndex];
            if (selectedOption) {
                subdistrictSearchInput.value = selectedOption.textContent;
            }
        }
    }
    
    // Helper function to find input element by name
    function findInputByName(name) {
      const form = document.getElementById('address-validator-form');
        if (!form) return null;
        return form.querySelector(`input[name="${name}"]`);
    }
  
    // Helper function to find postal code input by ID
    function findPostalCodeInput() {
      return document.querySelector('#address-postal-code');
    }
  
    // Helper: Set active field and highlight it
    function setActiveField(fieldType) {
        // Remove active class from all fields
        const allFields = ['subdistrict', 'district', 'province', 'postcode'];
        allFields.forEach(type => {
            const searchInput = getFieldSearchInput(type);
            if (searchInput) {
                const fieldControl = searchInput.closest('.form-control');
                if (fieldControl) {
                    fieldControl.classList.remove('field-active');
                }
            }
        });
        
        // Set new active field
        activeField = fieldType;
        const searchInput = getFieldSearchInput(fieldType);
        if (searchInput) {
            const fieldControl = searchInput.closest('.form-control');
            if (fieldControl) {
                fieldControl.classList.add('field-active');
            }
        }
    }
    
    // Helper: Remove active field highlighting
    function clearActiveField() {
        if (activeField) {
            const searchInput = getFieldSearchInput(activeField);
            if (searchInput) {
                const fieldControl = searchInput.closest('.form-control');
                if (fieldControl) {
                    fieldControl.classList.remove('field-active');
                }
            }
        }
        activeField = null;
    }
    
    // Helper: Get search input element by field type
    function getFieldSearchInput(fieldType) {
        const fieldMap = {
            'subdistrict': 'address-validator-subdistrict-search',
            'district': 'address-validator-district-search',
            'province': 'address-validator-province-search',
            'postcode': 'address-postal-code'
        };
        const inputId = fieldMap[fieldType];
        return inputId ? document.getElementById(inputId) : null;
    }
    
    // Helper: Get field-specific display value from dropdown list item
    // This extracts only the active field's value, not the full hierarchy
    function getFieldDisplayValueFromListItem(item, fieldType) {
        if (!item || !fieldType) return '';
        
        // For postcode, get directly from data attribute
        if (fieldType === 'postcode') {
            return item.getAttribute('data-postcode') || '';
        }
        
        // Get the composite key to lookup the original item
        const compositeKey = item.getAttribute('data-composite-key');
        if (compositeKey) {
            const originalItem = addressDataMap.get(compositeKey);
            if (originalItem) {
                // Map fieldType to the correct field name for getDisplayName
                let fieldName = '';
                if (fieldType === 'subdistrict') {
                    fieldName = 'subDistrict';
                } else if (fieldType === 'district') {
                    fieldName = 'district';
                } else if (fieldType === 'province') {
                    fieldName = 'province';
                }
                
                if (fieldName) {
                    return getDisplayName(originalItem, fieldName);
                }
            }
        }
        
        // Fallback: try to get from data attributes (for backward compatibility)
        if (fieldType === 'subdistrict') {
            return item.getAttribute('data-subdistrict-th') || '';
        } else if (fieldType === 'district') {
            return item.getAttribute('data-district-th') || '';
        } else if (fieldType === 'province') {
            // For province, we need to get the display name from the original item
            const provinceId = item.getAttribute('data-province-id');
            if (provinceId) {
                const originalItem = Array.from(addressDataMap.values()).find(dataItem => String(dataItem.provinceId) === provinceId);
                if (originalItem) {
                    return getDisplayName(originalItem, 'province');
                }
            }
        }
        
        return '';
    }
    
    /**
     * Filter rows + first typeahead highlight in one pass (Safari-friendly: list.children, no :not(.hidden) query).
     * @param {string|null|undefined} filterNeedleOverride — e.g. normalized postcode digits; omit to use searchInput value
     */
    function applyDropdownFilterAndHighlight(list, fieldType, searchInput, filterNeedleOverride) {
        if (!list || !addressData.length) return;
        
        if (list.dataset.addrVirtual === '1' && fieldType) {
            const needleRaw = filterNeedleOverride !== undefined && filterNeedleOverride !== null
                ? String(filterNeedleOverride)
                : (searchInput ? String(searchInput.value) : '');
            renderVirtualAddressFieldList(list, fieldType, needleRaw);
            const hasRows = list.querySelector('.searchable-dropdown-item');
            const hasHint = list.querySelector('.searchable-dropdown-hint');
            list.style.display = hasRows || hasHint ? 'block' : 'none';
            if (!searchInput) return;
            if (String(needleRaw || '').trim().length > 0) {
                const firstItem = list.querySelector('.searchable-dropdown-item');
                if (firstItem) {
                    highlightDropdownItem(list, firstItem);
                } else {
                    const highlighted = list.querySelector('.searchable-dropdown-item.highlighted');
                    if (highlighted) highlighted.classList.remove('highlighted');
                }
            } else {
                const highlighted = list.querySelector('.searchable-dropdown-item.highlighted');
                if (highlighted) highlighted.classList.remove('highlighted');
            }
            return;
        }
        
        const needle = filterNeedleOverride !== undefined && filterNeedleOverride !== null
            ? String(filterNeedleOverride).toLowerCase().trim()
            : (searchInput ? String(searchInput.value).trim().toLowerCase() : '');
        
        const kids = list.children;
        const n = kids.length;
        let visibleCount = 0;
        let firstMatch = null;
        
        for (let i = 0; i < n; i++) {
            const item = kids[i];
            if (!item.classList || !item.classList.contains('searchable-dropdown-item')) continue;
            
            if (!needle) {
                item.classList.remove('hidden');
                visibleCount++;
            } else {
                let fieldValueLower = item.getAttribute('data-filter-text');
                if (fieldValueLower === null) {
                    const fieldValue = getFieldDisplayValueFromListItem(item, fieldType);
                    fieldValueLower = fieldValue.toLowerCase().trim();
                }
                if (fieldValueLower.includes(needle)) {
                    item.classList.remove('hidden');
                    visibleCount++;
                    if (!firstMatch) {
                        firstMatch = item;
                    }
                } else {
                    item.classList.add('hidden');
                }
            }
        }
        
        list.style.display = visibleCount > 0 ? 'block' : 'none';
        
        if (!searchInput) return;
        
        if (needle.length > 0) {
            if (firstMatch) {
                highlightDropdownItem(list, firstMatch);
            } else {
                const highlighted = list.querySelector('.searchable-dropdown-item.highlighted');
                if (highlighted) highlighted.classList.remove('highlighted');
            }
        } else {
            const highlighted = list.querySelector('.searchable-dropdown-item.highlighted');
            if (highlighted) highlighted.classList.remove('highlighted');
        }
    }
    
    // Helper: Filter only (no highlight) — delegates to apply with no search input
    function filterDropdownByActiveField(list, searchTerm, fieldType) {
        const searchLower = searchTerm ? String(searchTerm).toLowerCase().trim() : '';
        applyDropdownFilterAndHighlight(list, fieldType, null, searchLower);
    }
    
    // Helper: Close all address field dropdowns (except the one being opened)
    function closeAllDropdowns(exceptListId) {
        const dropdownListIds = [
            'address-validator-subdistrict-list',
            'address-validator-district-list',
            'address-validator-province-list',
            'address-postal-code-list'
        ];
        
        dropdownListIds.forEach(listId => {
            if (listId !== exceptListId) {
                const list = document.getElementById(listId);
                if (list) {
                    list.style.display = 'none';
                }
            }
        });
    }
  
    // Helper: Show all dropdown items and display dropdown
    function showAllDropdownItems(list) {
        const kids = list.children;
        for (let i = 0; i < kids.length; i++) {
            const el = kids[i];
            if (el.classList && el.classList.contains('searchable-dropdown-item')) {
                el.classList.remove('hidden');
            }
        }
        list.style.display = 'block';
    }
    
    /**
     * Distinguish scroll from tap on a scrollable dropdown list (Mobile Safari fires touchend on a row after scrolling).
     */
    function createListScrollVsTapGuard() {
        const SLOP_PX = 14;
        let startX = null;
        let startY = null;
        let exceededSlop = false;
        let suppressClickUntil = 0;
        return {
            touchStart: function (e) {
                if (e.touches && e.touches.length === 1) {
                    startX = e.touches[0].clientX;
                    startY = e.touches[0].clientY;
                    exceededSlop = false;
                }
            },
            touchMove: function (e) {
                if (startX === null || startY === null || !e.touches || !e.touches.length) return;
                const t = e.touches[0];
                if (Math.abs(t.clientX - startX) > SLOP_PX || Math.abs(t.clientY - startY) > SLOP_PX) {
                    exceededSlop = true;
                }
            },
            /** @returns {boolean} true if this gesture was a scroll — caller should skip row pick */
            consumeIfScrollGesture: function () {
                const wasScroll = exceededSlop;
                startX = null;
                startY = null;
                exceededSlop = false;
                if (wasScroll) {
                    suppressClickUntil = Date.now() + 500;
                }
                return wasScroll;
            },
            /** iOS may still emit click after a scroll; ignore picks in that window */
            shouldSuppressSyntheticClick: function () {
                return Date.now() < suppressClickUntil;
            },
            reset: function () {
                startX = null;
                startY = null;
                exceededSlop = false;
            }
        };
    }
    
    // Helper: Get field-specific display value from option element
    // This ensures we compare only the active field's value, not other fields
    function getFieldDisplayValueFromOption(option, fieldType) {
        if (!option || !fieldType) return '';
        
        // Options store field-specific values in textContent and data attributes
        // Use data attributes based on current language for accurate comparison
        const langAttr = currentLanguage === 'th' ? 'data-th-name' : 'data-en-name';
        let fieldValue = '';
        
        if (fieldType === 'subdistrict') {
            // Try data attribute first, fallback to textContent
            fieldValue = option.getAttribute(langAttr) || option.textContent.trim();
        } else if (fieldType === 'district') {
            fieldValue = option.getAttribute(langAttr) || option.textContent.trim();
        } else if (fieldType === 'province') {
            fieldValue = option.getAttribute(langAttr) || option.textContent.trim();
        }
        
        return fieldValue.trim();
    }
    
    // Validate searchable dropdown input - check if value matches a valid option
    // IMPORTANT: Validation is based ONLY on the active field's value
    // Does NOT check or filter based on other field values
    function validateSearchableDropdownInput(searchInputId, selectId) {
        const searchInput = document.getElementById(searchInputId);
        const select = document.getElementById(selectId);
        
        if (!searchInput || !select) return false;
        
        const inputValue = searchInput.value.trim();
        const selectValue = select.value;
        
        // If input is empty, validation will be handled by form validation
        if (inputValue === '') {
            return selectValue === '';
        }
        
        // Determine field type based on select ID
        let fieldType = '';
        let fieldName = '';
        if (selectId === 'address-validator-province-select') {
            fieldType = 'province';
            fieldName = 'select[name="address[province]"]';
        } else if (selectId === 'address-validator-district-select') {
            fieldType = 'district';
            fieldName = 'select[name="address[district]"]';
        } else if (selectId === 'address-validator-subdistrict-select') {
            fieldType = 'subdistrict';
            fieldName = 'select[name="address[subdistrict]"]';
        }
        
        const options = Array.from(select.options);
        const inputText = inputValue.toLowerCase();
        let matchingOption = null;
        let wasManuallyTyped = false;
        
        // First, try to find option by select value (if user selected from dropdown)
        if (selectValue) {
            matchingOption = options.find(opt => opt.value === selectValue && !opt.disabled && opt.value !== '');
        }
        
        // If no match found by select value, search all options by field-specific value
        // This handles the case when user types manually (select value might not be set yet)
        if (!matchingOption) {
            wasManuallyTyped = true; // Mark as manually typed since we had to search
            matchingOption = options.find(opt => {
                if (!opt.value || opt.disabled || opt.value === '') return false;
                // Get field-specific value from option (language-aware)
                const optionFieldValue = getFieldDisplayValueFromOption(opt, fieldType);
                return optionFieldValue.toLowerCase() === inputText;
            });
            
            // If we found a match by field value, update the hidden select
            if (matchingOption) {
                select.value = matchingOption.value;
                select.selectedIndex = Array.from(select.options).indexOf(matchingOption);
            }
        } else {
            // Found by select value - check if input matches (might be manually edited after selection)
            const optionFieldValue = getFieldDisplayValueFromOption(matchingOption, fieldType);
            if (optionFieldValue.toLowerCase() !== inputText) {
                // Input doesn't match selected option - user might have manually edited
                // Search again to find the correct option
                wasManuallyTyped = true;
                const newMatch = options.find(opt => {
                    if (!opt.value || opt.disabled || opt.value === '') return false;
                    const optFieldValue = getFieldDisplayValueFromOption(opt, fieldType);
                    return optFieldValue.toLowerCase() === inputText;
                });
                if (newMatch) {
                    matchingOption = newMatch;
                    select.value = newMatch.value;
                    select.selectedIndex = Array.from(select.options).indexOf(newMatch);
                }
            }
        }
        
        // If no matching option found at all, it's invalid
        if (!matchingOption) {
            if (fieldName) {
                showFieldError(fieldName, 'Please select an item from the list');
            }
            return false;
        }
        
        // Get field-specific value from the matching option
        const optionFieldValue = getFieldDisplayValueFromOption(matchingOption, fieldType);
        const optionText = optionFieldValue.toLowerCase();
        
        // Compare only the field-specific value
        // Do NOT check other field values - validation is independent per field
        if (optionText !== inputText) {
            // Input doesn't match option's field-specific value - invalid
            if (fieldName) {
                showFieldError(fieldName, 'Please select an item from the list');
            }
            return false;
        }
        
        // If validation passed and user typed manually (not selected from dropdown),
        // auto-map related fields to keep them in sync and prevent mismatched combinations
        // Example: If user types District "B" that doesn't match Sub District "A",
        // auto-update Sub District to match District "B"
        if (matchingOption && fieldType && wasManuallyTyped) {
            // Get the full address data for this option to auto-map related fields
            const optionValue = matchingOption.value;
            
            // Try to get data from option's data attributes first (faster than searching)
            let storedData = {
                subdistrictTh: matchingOption.getAttribute('data-subdistrict-th') || '',
                districtTh: matchingOption.getAttribute('data-district-th') || '',
                provinceId: matchingOption.getAttribute('data-province-id') || '',
                postcode: matchingOption.getAttribute('data-postcode') || ''
            };
            
            // If data attributes are incomplete, fallback to searching addressData
            if (!storedData.provinceId && !storedData.districtTh && !storedData.subdistrictTh) {
                const matchingAddressData = addressData.find(item => {
                    if (fieldType === 'subdistrict') {
                        return String(item.subDistrictTh) === optionValue;
                    } else if (fieldType === 'district') {
                        return String(item.districtTh) === optionValue;
                    } else if (fieldType === 'province') {
                        return String(item.provinceId) === optionValue;
                    }
                    return false;
                });
                
                if (matchingAddressData) {
                    storedData = {
                        subdistrictTh: matchingAddressData.subDistrictTh || '',
                        districtTh: matchingAddressData.districtTh || '',
                        provinceId: String(matchingAddressData.provinceId || ''),
                        postcode: String(matchingAddressData.postcode || '')
                    };
                }
            }
            
            // Only auto-map if we have valid stored data and not already auto-mapping
            if (!isAutoMapping && (storedData.provinceId || storedData.districtTh || storedData.subdistrictTh || storedData.postcode)) {
                // Build composite key for fast lookup
                const compositeKey = [
                    String(storedData.subdistrictTh || '').trim(),
                    String(storedData.districtTh || '').trim(),
                    String(storedData.provinceId || '').trim(),
                    String(storedData.postcode || '').trim()
                ].join('|');
                
                const addressItem = compositeKey ? addressDataMap.get(compositeKey) : null;
                
                // Use instant lookup (no delay needed with addressItem)
                autoMapRelatedFieldsWithData(fieldType, optionValue, storedData, addressItem);
            }
        }
        
        // Clear error if valid
        clearFieldErrorForSearchableDropdown(searchInputId, selectId);
        return true;
    }
    
    // Validate that select value exists in valid options (for autofill protection)
    function validateSelectValueExists(selectId) {
        const select = document.getElementById(selectId);
        if (!select) return false;
        
        const selectValue = select.value;
        if (!selectValue || selectValue === '') return false;
        
        const options = Array.from(select.options);
        const validOption = options.find(opt => 
            opt.value === selectValue && 
            !opt.disabled && 
            opt.value !== ''
        );
        
        return !!validOption;
    }
    
    // Clear field error for searchable dropdown
    function clearFieldErrorForSearchableDropdown(searchInputId, selectId) {
        const select = document.getElementById(selectId);
        if (!select) return;
        
        // Determine field name based on select ID
        let fieldName = '';
        if (selectId === 'address-validator-province-select') {
            fieldName = 'select[name="address[province]"]';
        } else if (selectId === 'address-validator-district-select') {
            fieldName = 'select[name="address[district]"]';
        } else if (selectId === 'address-validator-subdistrict-select') {
            fieldName = 'select[name="address[subdistrict]"]';
        }
        
        if (fieldName) {
            clearFieldError(fieldName);
        }
    }
    
    // Clear all address field errors (subdistrict, district, province, postcode)
    // This is useful when selecting from any address field since they auto-map to each other
    function clearAllAddressFieldErrors() {
        clearFieldError('select[name="address[subdistrict]"]');
        clearFieldError('select[name="address[district]"]');
        clearFieldError('select[name="address[province]"]');
        clearFieldError('address[zip]');
    }
    
    // Auto-map related fields when selecting from any field (with stored hierarchy data)
    // addressItem: optional full address data item for instant display name lookup (avoids array search)
    function autoMapRelatedFieldsWithData(selectedFieldType, selectedValue, storedData, addressItem) {
        if (isAutoMapping || !addressData.length || !selectedValue) return;
        
        isAutoMapping = true;
        
        try {
            // Helper to get display name quickly - use addressItem if available, otherwise fallback to search
            const getDisplayNameFast = (fieldType, value) => {
                if (addressItem) {
                    // Use the provided address item directly (instant, no search)
                    return getDisplayName(addressItem, fieldType);
                }
                // Fallback to searching (slower but works if addressItem not provided)
                return getDisplayNameFromData(fieldType, value);
            };
            
            const hierarchyRow =
                addressItem ||
                (storedData
                    ? addressDataMap.get(
                          [
                              String(storedData.subdistrictTh || '').trim(),
                              String(storedData.districtTh || '').trim(),
                              String(storedData.provinceId || '').trim(),
                              String(storedData.postcode || '').trim()
                          ].join('|')
                      )
                    : null);
            
            // Helper to ensure dropdown is populated before setting value
            const ensureAndSetFieldValue = (fieldType, selectValue, displayText) => {
                if (usesVirtualAddressLists() && hierarchyRow) {
                    setFieldValueFromAddressRow(fieldType, hierarchyRow);
                    return;
                }
                const fieldMap = {
                    'subdistrict': 'address-validator-subdistrict-select',
                    'district': 'address-validator-district-select',
                    'province': 'address-validator-province-select',
                    'postcode': null
                };
                
                const selectId = fieldMap[fieldType];
                
                // For postcode, we can set it directly (no select dropdown)
                if (fieldType === 'postcode') {
                    if (!dropdownsPopulated.postcode) {
                        ensureDropdownPopulated('postcode');
                    }
                    setFieldValue(fieldType, selectValue, displayText);
                    return;
                }
                
                // For other fields, ensure dropdown is populated and wait for option to exist
                if (!selectId) return;
                
                // Ensure dropdown is populated
                if (!dropdownsPopulated[fieldType]) {
                    ensureDropdownPopulated(fieldType);
                }
                
                // Wait for the option to exist in the dropdown (handles async population)
                const select = document.getElementById(selectId);
                if (!select) return;
                
                // Check if option already exists
                let option = Array.from(select.options).find(opt => 
                    opt.value === selectValue || 
                    opt.getAttribute('data-th-name') === selectValue
                );
                
                if (option) {
                    // Option exists, set value immediately
                    setFieldValue(fieldType, selectValue, displayText);
                } else {
                    // Option doesn't exist yet, wait for dropdown to be populated
                    // This handles the case where ensureDropdownPopulated is async
                    let attempts = 0;
                    const maxAttempts = 20; // Wait up to 1 second (20 * 50ms)
                    
                    const checkAndSet = () => {
                        attempts++;
                        option = Array.from(select.options).find(opt => 
                            opt.value === selectValue || 
                            opt.getAttribute('data-th-name') === selectValue
                        );
                        
                        if (option) {
                            // Option found, set value
                            setFieldValue(fieldType, selectValue, displayText);
                        } else if (attempts < maxAttempts && (!dropdownsPopulated[fieldType] || attempts < 5)) {
                            // Keep checking if:
                            // 1. Dropdown not yet populated, OR
                            // 2. Dropdown just populated (first few attempts) - give it a chance to find the option
                            setTimeout(checkAndSet, 50);
                        } else {
                            // Timeout or dropdown populated but option not found after several attempts
                            // Set the search input value anyway (user can see it)
                            setFieldValue(fieldType, selectValue, displayText);
                        }
                    };
                    
                    // Start checking
                    setTimeout(checkAndSet, 50);
                }
            };
            
            // Use stored data for more accurate mapping
            switch (selectedFieldType) {
                case 'subdistrict':
                    if (storedData.districtTh) {
                        ensureAndSetFieldValue('district', storedData.districtTh, getDisplayNameFast('district', storedData.districtTh));
                    }
                    if (storedData.provinceId) {
                        ensureAndSetFieldValue('province', storedData.provinceId, getDisplayNameFast('province', storedData.provinceId));
                    }
                    if (storedData.postcode) {
                        ensureAndSetFieldValue('postcode', storedData.postcode, storedData.postcode);
                    }
                    break;
                    
                case 'district':
                    if (storedData.subdistrictTh) {
                        ensureAndSetFieldValue('subdistrict', storedData.subdistrictTh, getDisplayNameFast('subDistrict', storedData.subdistrictTh));
                    }
                    if (storedData.provinceId) {
                        ensureAndSetFieldValue('province', storedData.provinceId, getDisplayNameFast('province', storedData.provinceId));
                    }
                    if (storedData.postcode) {
                        ensureAndSetFieldValue('postcode', storedData.postcode, storedData.postcode);
                    }
                    break;
                    
                case 'province':
                    if (storedData.subdistrictTh) {
                        ensureAndSetFieldValue('subdistrict', storedData.subdistrictTh, getDisplayNameFast('subDistrict', storedData.subdistrictTh));
                    }
                    if (storedData.districtTh) {
                        ensureAndSetFieldValue('district', storedData.districtTh, getDisplayNameFast('district', storedData.districtTh));
                    }
                    if (storedData.postcode) {
                        ensureAndSetFieldValue('postcode', storedData.postcode, storedData.postcode);
                    }
                    break;
                    
                case 'postcode':
                    if (storedData.subdistrictTh) {
                        ensureAndSetFieldValue('subdistrict', storedData.subdistrictTh, getDisplayNameFast('subDistrict', storedData.subdistrictTh));
                    }
                    if (storedData.districtTh) {
                        ensureAndSetFieldValue('district', storedData.districtTh, getDisplayNameFast('district', storedData.districtTh));
                    }
                    if (storedData.provinceId) {
                        ensureAndSetFieldValue('province', storedData.provinceId, getDisplayNameFast('province', storedData.provinceId));
                    }
                    break;
            }
        } finally {
            // Reset auto-mapping flag immediately (no delay needed with instant lookup)
            isAutoMapping = false;
        }
    }
    
    // Helper: Get display name from stored data or addressData
    function getDisplayNameFromData(fieldType, value) {
        if (!value || !addressData.length) return '';
        
        const match = addressData.find(item => {
            if (fieldType === 'subDistrict') return String(item.subDistrictTh) === value;
            if (fieldType === 'district') return String(item.districtTh) === value;
            if (fieldType === 'province') return String(item.provinceId) === value;
            return false;
        });
        
        return match ? getDisplayName(match, fieldType) : '';
    }
    
    // Auto-map related fields when selecting from any field
    function autoMapRelatedFields(selectedFieldType, selectedValue) {
        if (isAutoMapping || !addressData.length || !selectedValue) return;
        
        isAutoMapping = true;
        
        try {
            // Helper to ensure dropdown is populated before setting value
            const ensureAndSetFieldValue = (fieldType, selectValue, displayText, sourceRow) => {
                if (usesVirtualAddressLists() && sourceRow) {
                    setFieldValueFromAddressRow(fieldType, sourceRow);
                    return;
                }
                const fieldMap = {
                    'subdistrict': 'address-validator-subdistrict-select',
                    'district': 'address-validator-district-select',
                    'province': 'address-validator-province-select',
                    'postcode': null
                };
                
                const selectId = fieldMap[fieldType];
                
                // For postcode, we can set it directly (no select dropdown)
                if (fieldType === 'postcode') {
                    if (!dropdownsPopulated.postcode) {
                        ensureDropdownPopulated('postcode');
                    }
                    setFieldValue(fieldType, selectValue, displayText);
                    return;
                }
                
                // For other fields, ensure dropdown is populated and wait for option to exist
                if (!selectId) return;
                
                // Ensure dropdown is populated
                if (!dropdownsPopulated[fieldType]) {
                    ensureDropdownPopulated(fieldType);
                }
                
                // Wait for the option to exist in the dropdown (handles async population)
                const select = document.getElementById(selectId);
                if (!select) return;
                
                // Check if option already exists
                let option = Array.from(select.options).find(opt => 
                    opt.value === selectValue || 
                    opt.getAttribute('data-th-name') === selectValue
                );
                
                if (option) {
                    // Option exists, set value immediately
                    setFieldValue(fieldType, selectValue, displayText);
                } else {
                    // Option doesn't exist yet, wait for dropdown to be populated
                    // This handles the case where ensureDropdownPopulated is async
                    let attempts = 0;
                    const maxAttempts = 20; // Wait up to 1 second (20 * 50ms)
                    
                    const checkAndSet = () => {
                        attempts++;
                        option = Array.from(select.options).find(opt => 
                            opt.value === selectValue || 
                            opt.getAttribute('data-th-name') === selectValue
                        );
                        
                        if (option) {
                            // Option found, set value
                            setFieldValue(fieldType, selectValue, displayText);
                        } else if (attempts < maxAttempts && (!dropdownsPopulated[fieldType] || attempts < 5)) {
                            // Keep checking if:
                            // 1. Dropdown not yet populated, OR
                            // 2. Dropdown just populated (first few attempts) - give it a chance to find the option
                            setTimeout(checkAndSet, 50);
                        } else {
                            // Timeout or dropdown populated but option not found after several attempts
                            // Set the search input value anyway (user can see it)
                            setFieldValue(fieldType, selectValue, displayText);
                        }
                    };
                    
                    // Start checking
                    setTimeout(checkAndSet, 50);
                }
            };
            
            let match = null;
            
            // Find matching address data based on selected field
            switch (selectedFieldType) {
                case 'subdistrict':
                    match = addressData.find(item => String(item.subDistrictTh) === selectedValue);
                    if (match) {
                        // Auto-fill: District, Province, Postcode
                        ensureAndSetFieldValue('district', match.districtTh, getDisplayName(match, 'district'), match);
                        ensureAndSetFieldValue('province', String(match.provinceId), getDisplayName(match, 'province'), match);
                        ensureAndSetFieldValue('postcode', String(match.postcode), String(match.postcode), match);
                    }
                    break;
                    
                case 'district':
                    // For district, we might have multiple matches, so pick the first one
                    match = addressData.find(item => String(item.districtTh) === selectedValue);
                    if (match) {
                        // Auto-fill: Sub District, Province, Postcode
                        // For sub-district, we need to find a unique one or pick first
                        const subdistrictMatch = addressData.find(item => 
                            String(item.districtTh) === selectedValue && 
                            String(item.provinceId) === String(match.provinceId)
                        );
                        if (subdistrictMatch) {
                            ensureAndSetFieldValue('subdistrict', subdistrictMatch.subDistrictTh, getDisplayName(subdistrictMatch, 'subDistrict'), subdistrictMatch);
                        }
                        ensureAndSetFieldValue('province', String(match.provinceId), getDisplayName(match, 'province'), match);
                        // For postcode, use the first match's postcode
                        if (subdistrictMatch) {
                            ensureAndSetFieldValue('postcode', String(subdistrictMatch.postcode), String(subdistrictMatch.postcode), subdistrictMatch);
                        }
                    }
                    break;
                    
                case 'province':
                    // For province, pick the first match
                    match = addressData.find(item => String(item.provinceId) === selectedValue);
                    if (match) {
                        // Auto-fill: Sub District, District, Postcode
                        // Pick first sub-district and district for this province
                        ensureAndSetFieldValue('subdistrict', match.subDistrictTh, getDisplayName(match, 'subDistrict'), match);
                        ensureAndSetFieldValue('district', match.districtTh, getDisplayName(match, 'district'), match);
                        ensureAndSetFieldValue('postcode', String(match.postcode), String(match.postcode), match);
                    }
                    break;
                    
                case 'postcode':
                    // For postcode, find matching address
                    match = addressData.find(item => String(item.postcode) === selectedValue);
                    if (match) {
                        // Auto-fill: Sub District, District, Province
                        ensureAndSetFieldValue('subdistrict', match.subDistrictTh, getDisplayName(match, 'subDistrict'), match);
                        ensureAndSetFieldValue('district', match.districtTh, getDisplayName(match, 'district'), match);
                        ensureAndSetFieldValue('province', String(match.provinceId), getDisplayName(match, 'province'), match);
                    }
                    break;
            }
        } finally {
            // Reset flag after a short delay to allow all updates to complete
            setTimeout(() => {
                isAutoMapping = false;
            }, 100);
        }
    }
    
    // Helper: Set field value (both select and search input)
    function setFieldValue(fieldType, selectValue, displayText) {
        const fieldMap = {
            'subdistrict': {
                searchInputId: 'address-validator-subdistrict-search',
                selectId: 'address-validator-subdistrict-select'
            },
            'district': {
                searchInputId: 'address-validator-district-search',
                selectId: 'address-validator-district-select'
            },
            'province': {
                searchInputId: 'address-validator-province-search',
                selectId: 'address-validator-province-select'
            },
            'postcode': {
                searchInputId: 'address-postal-code',
                selectId: null
            }
        };
        
        const fieldConfig = fieldMap[fieldType];
        if (!fieldConfig) return;
        
        const searchInput = document.getElementById(fieldConfig.searchInputId);
        if (searchInput) {
            searchInput.value = displayText || '';
            if (fieldType !== 'postcode') {
                searchInput.removeAttribute('readonly');
                searchInput.disabled = false;
            }
        }
        
        if (fieldConfig.selectId) {
            const select = document.getElementById(fieldConfig.selectId);
            if (select && selectValue) {
                // Find matching option by value
                let option = Array.from(select.options).find(opt => opt.value === selectValue);
                
                // If not found by value, try to find by Thai name attribute
                if (!option) {
                    option = Array.from(select.options).find(opt => 
                        opt.getAttribute('data-th-name') === selectValue
                    );
                }
                
                if (option) {
                    select.value = option.value;
                    // Update search input to match the option text
                    if (searchInput) {
                        searchInput.value = option.textContent;
                    }
                    // Don't trigger change event during auto-mapping to avoid loops
                    // The change handlers will update dependent fields if needed
                } else {
                    // Option not found - this shouldn't happen if all options are populated
                    // But set the search input anyway
                    if (searchInput && displayText) {
                        searchInput.value = displayText;
                    }
                }
            }
        }
    }
    
    // Initialize searchable dropdown
    function initSearchableDropdown(searchInputId, selectId, listId, fieldType) {
        /** After typing pauses this long, run list match validation (province / district / subdistrict only). */
        const SEARCHABLE_DROPDOWN_VALIDATE_DEBOUNCE_MS = 500;
        
        const searchInput = document.getElementById(searchInputId);
        const select = document.getElementById(selectId);
        const list = document.getElementById(listId);
        
        if (!searchInput) {
            return;
        }
        if (!select) {
            return;
        }
        if (!list) {
            return;
        }
        
        list.style.touchAction = 'manipulation';
        
        // Track if dropdown should stay open
        let keepDropdownOpen = false;
        let filterAndHighlightRaf = null;
        let virtualListFilterTimer = null;
        let lastSearchableListPickAt = 0;
        const wrapper = searchInput.closest('.searchable-dropdown-wrapper') || searchInput.closest('.form-control');
        
        // Function to close dropdown
        function closeDropdown() {
            keepDropdownOpen = false;
            list.style.display = 'none';
        }
        
        // Function to open dropdown
        function openDropdown() {
            if (Date.now() < suppressAddressValidatorDropdownOpenUntil) {
                list.style.display = 'none';
                return;
            }
            // LAZY LOADING: Populate dropdown if not already populated
            if (fieldType && !dropdownsPopulated[fieldType]) {
                ensureDropdownPopulated(fieldType);
            }
            
            keepDropdownOpen = true;
            // Close all other dropdowns first
            closeAllDropdowns(listId);
            if (list.dataset.addrVirtual === '1' && fieldType) {
                renderVirtualAddressFieldList(list, fieldType, searchInput.value || '');
                const hasRows = list.querySelector('.searchable-dropdown-item');
                const hasHint = list.querySelector('.searchable-dropdown-hint');
                list.style.display = hasRows || hasHint ? 'block' : 'none';
            } else {
                // Show dropdown immediately (before refreshing HTML to avoid delay)
                showAllDropdownItems(list);
                // Refresh HTML to bold the segment matching the active field (defer to avoid blocking, especially on mobile)
                if (fieldType) {
                    requestAnimationFrame(() => {
                        refreshDropdownHTML(list, fieldType);
                    });
                }
            }
            // Mobile: openDropdown can run from touchend before the browser focuses the input — re-assert focus in the same turn
            if (document.activeElement !== searchInput && !searchInput.disabled) {
                try {
                    searchInput.focus({ preventScroll: true });
                } catch (err) {
                    searchInput.focus();
                }
            }
        }
        
        /** When focus opened a hidden list just before touchend, skip touch-close so we don't undo that open. */
        let lastOpenedFromFocusAt = 0;
        
        // Show/hide dropdown on focus/blur - set active field and show all items
        searchInput.addEventListener('focus', function() {
            // Set this field as active
            if (fieldType) {
                setActiveField(fieldType);
            }
            const listWasHidden = list.style.display === 'none' || list.style.display === '';
            openDropdown();
            if (listWasHidden) {
                lastOpenedFromFocusAt = Date.now();
            }
        });
        
        // Track touch position to distinguish taps from scrolls
        let touchStartY = null;
        let touchStartX = null;
        let isScrolling = false;
        /** Legacy only: iOS may emit mousedown after touch; ignored when PointerEvent is used. */
        let suppressMouseToggleUntil = 0;
        
        function handleOpenClosePointerDown(e) {
            if (e.pointerType === 'touch') {
                return;
            }
            e.stopPropagation();
            if (list.style.display === 'none' || list.style.display === '') {
                keepDropdownOpen = true;
                openDropdown();
            } else {
                closeDropdown();
            }
        }
        
        function handleOpenCloseMouseDown(e) {
            e.stopPropagation();
            if (Date.now() < suppressMouseToggleUntil) {
                return;
            }
            if (list.style.display === 'none' || list.style.display === '') {
                keepDropdownOpen = true;
                openDropdown();
            } else {
                closeDropdown();
            }
        }
        
        if (typeof PointerEvent !== 'undefined') {
            searchInput.addEventListener('pointerdown', handleOpenClosePointerDown);
        } else {
            searchInput.addEventListener('mousedown', handleOpenCloseMouseDown);
        }
        
        // Handle touch events with scroll detection for mobile
        searchInput.addEventListener('touchstart', function(e) {
            // Record touch start position
            const touch = e.touches[0];
            touchStartY = touch.clientY;
            touchStartX = touch.clientX;
            isScrolling = false;
        }, { passive: true });
        
        // Detect if user is scrolling (not tapping)
        searchInput.addEventListener('touchmove', function(e) {
            if (touchStartY !== null && touchStartX !== null) {
                const touch = e.touches[0];
                const deltaY = Math.abs(touch.clientY - touchStartY);
                const deltaX = Math.abs(touch.clientX - touchStartX);
                
                // If movement is significant, it's a scroll, not a tap
                if (deltaY > 5 || deltaX > 5) {
                    isScrolling = true;
                    // If dropdown is open and user is scrolling, close it
                    if (list.style.display === 'block' || list.style.display === '') {
                        closeDropdown();
                    }
                }
            }
        }, { passive: true });
        
        // Tap: open if closed; close if open unless focus just opened the list (focus-before-touchend ordering).
        searchInput.addEventListener('touchend', function(e) {
            if (!isScrolling && touchStartY !== null) {
                e.stopPropagation();
                if (list.style.display === 'none' || list.style.display === '') {
                    keepDropdownOpen = true;
                    openDropdown();
                    if (typeof PointerEvent === 'undefined') {
                        suppressMouseToggleUntil = Date.now() + 500;
                    }
                } else if (Date.now() - lastOpenedFromFocusAt > 80) {
                    closeDropdown();
                }
            }
            touchStartY = null;
            touchStartX = null;
            isScrolling = false;
        }, { passive: false });
        
        // Also handle click for toggle behavior
        searchInput.addEventListener('click', function(e) {
            e.stopPropagation();
        });
        
        const listScrollPickGuard = createListScrollVsTapGuard();
        
        // Prevent blur when clicking on list
        list.addEventListener('mousedown', function(e) {
            e.preventDefault(); // Prevent input blur
            keepDropdownOpen = true;
        });
        list.addEventListener('touchstart', function(e) {
            keepDropdownOpen = true;
            listScrollPickGuard.touchStart(e);
        }, { passive: true });
        list.addEventListener('touchmove', function(e) {
            listScrollPickGuard.touchMove(e);
        }, { passive: true });
        list.addEventListener('touchcancel', function () {
            listScrollPickGuard.reset();
        }, { passive: true });
        
        searchInput.addEventListener('blur', function(e) {
            // Don't close if focus is moving to an element within the wrapper
            const relatedTarget = e.relatedTarget;
            if (relatedTarget && wrapper && wrapper.contains(relatedTarget)) {
                keepDropdownOpen = true;
                return;
            }
            
            // Delay hiding to allow click on list item
            setTimeout(() => {
                if (!keepDropdownOpen) {
                    closeDropdown();
                    // Validate if input value matches a valid option
                    validateSearchableDropdownInput(searchInputId, selectId);
                    // Clear active field highlighting
                    if (activeField === fieldType) {
                        clearActiveField();
                    }
                }
                keepDropdownOpen = false;
            }, 200);
        });
        
        // Handle input - filter + typeahead batched to one rAF (was: duplicate input listeners + sync filter every key)
        let autofillCheckTimeout;
        searchInput.addEventListener('input', function() {
            if (!this.readOnly) {
                // Set active field if not already set
                if (fieldType && activeField !== fieldType) {
                    setActiveField(fieldType);
                }
                
                // Close all other dropdowns when typing in this field
                closeAllDropdowns(listId);
                
                const searchTerm = searchInput.value.trim();
                
                // Clear hidden select if search input is empty
                if (searchTerm === '') {
                    select.value = '';
                    select.selectedIndex = 0;
                    select.removeAttribute('data-selected-composite');
                } else {
                    // Clear error when user starts typing (will be validated on blur)
                    clearFieldErrorForSearchableDropdown(searchInputId, selectId);
                }
                
                if (list.dataset.addrVirtual === '1' && fieldType) {
                    if (filterAndHighlightRaf) cancelAnimationFrame(filterAndHighlightRaf);
                    filterAndHighlightRaf = null;
                    if (virtualListFilterTimer) clearTimeout(virtualListFilterTimer);
                    virtualListFilterTimer = setTimeout(function () {
                        virtualListFilterTimer = null;
                        if (list.dataset.addrVirtual !== '1') return;
                        applyDropdownFilterAndHighlight(list, fieldType, searchInput);
                    }, ADDR_VIRTUAL_INPUT_DEBOUNCE_MS);
                } else {
                    if (filterAndHighlightRaf) cancelAnimationFrame(filterAndHighlightRaf);
                    filterAndHighlightRaf = requestAnimationFrame(function () {
                        filterAndHighlightRaf = null;
                        if (fieldType) {
                            applyDropdownFilterAndHighlight(list, fieldType, searchInput);
                        } else {
                            showAllDropdownItems(list);
                            updateTypeaheadHighlightForList(list, searchInput);
                        }
                    });
                }
            }
            
            // Check for autofill - validate after a delay
            if (autofillCheckTimeout) {
                clearTimeout(autofillCheckTimeout);
            }
            
            autofillCheckTimeout = setTimeout(() => {
                // If input has value, validate it matches a valid option
                if (searchInput.value.trim() !== '') {
                    validateSearchableDropdownInput(searchInputId, selectId);
                }
            }, SEARCHABLE_DROPDOWN_VALIDATE_DEBOUNCE_MS);
        });
        
        // Handle selection from list — touchend + preventDefault removes mobile ~300ms click delay
        function handleSearchableListItemPick(e) {
            const item = e.target.closest('.searchable-dropdown-item');
            if (!item) return;
            
            const compositeKey = item.getAttribute('data-composite-key');
            const dataItem = compositeKey ? addressDataMap.get(compositeKey) : null;
            const value = item.getAttribute('data-value');
            const text = item.textContent.trim();
            
            if (!value || value === 'null' || value === 'undefined') {
                return;
            }
            
            const displayFromItem =
                dataItem && fieldType
                    ? getDisplayName(
                          dataItem,
                          fieldType === 'subdistrict'
                              ? 'subDistrict'
                              : fieldType === 'district'
                                ? 'district'
                                : 'province'
                      )
                    : '';
            
            if (dataItem && fieldType && select) {
                if (
                    select.getAttribute('data-selected-composite') === compositeKey &&
                    searchInput.value.trim() === (displayFromItem || text).trim()
                ) {
                    lastSearchableListPickAt = Date.now();
                    closeDropdown();
                    return;
                }
            } else if (select.value === value && searchInput.value.trim() === text) {
                lastSearchableListPickAt = Date.now();
                closeDropdown();
                return;
            }
            
            lastSearchableListPickAt = Date.now();
            
            if (dataItem && fieldType) {
                syncSelectToSingleAddressItem(select, dataItem, fieldType);
                if (compositeKey) {
                    select.setAttribute('data-selected-composite', compositeKey);
                } else {
                    select.removeAttribute('data-selected-composite');
                }
                searchInput.value = (displayFromItem || text).trim();
            } else {
                let optionFound = false;
                const options = Array.from(select.options);
                let selectedOption = null;
                
                for (let i = 0; i < options.length; i++) {
                    if (options[i].value === value) {
                        select.selectedIndex = i;
                        select.value = value;
                        selectedOption = options[i];
                        optionFound = true;
                        break;
                    }
                }
                
                if (!optionFound) {
                    for (let i = 0; i < options.length; i++) {
                        if (options[i].textContent.trim() === text) {
                            select.selectedIndex = i;
                            select.value = options[i].value;
                            selectedOption = options[i];
                            optionFound = true;
                            break;
                        }
                    }
                }
                
                if (optionFound && selectedOption) {
                    const currentValue = select.value;
                    const expectedValue = value;
                    if (currentValue !== expectedValue) {
                        select.value = expectedValue;
                    }
                    
                    const fieldDisplayValue = getFieldDisplayValueFromOption(selectedOption, fieldType);
                    searchInput.value = fieldDisplayValue || selectedOption.textContent.trim();
                } else {
                    const fieldValue = text.split(' > ')[0];
                    searchInput.value = fieldValue;
                }
            }
            
            clearAllAddressFieldErrors();
            
            if (fieldType && value) {
                const storedData = {
                    subdistrictTh: item.getAttribute('data-subdistrict-th'),
                    districtTh: item.getAttribute('data-district-th'),
                    provinceId: item.getAttribute('data-province-id'),
                    postcode: item.getAttribute('data-postcode')
                };
                
                const addressItem = compositeKey ? addressDataMap.get(compositeKey) : null;
                
                if (storedData.provinceId || storedData.districtTh || storedData.subdistrictTh || storedData.postcode) {
                    autoMapRelatedFieldsWithData(fieldType, value, storedData, addressItem);
                } else {
                    autoMapRelatedFields(fieldType, value);
                }
            }
            
            select.dispatchEvent(new Event('change', { bubbles: true }));
            
            keepDropdownOpen = false;
            list.style.display = 'none';
            
            if (activeField === fieldType) {
                clearActiveField();
            }
        }
        
        list.addEventListener('touchend', function(e) {
            if (listScrollPickGuard.consumeIfScrollGesture()) return;
            const item = e.target.closest('.searchable-dropdown-item');
            if (!item) return;
            e.stopPropagation();
            e.preventDefault();
            handleSearchableListItemPick(e);
        }, { passive: false });
        
        list.addEventListener('click', function(e) {
            e.stopPropagation();
            if (listScrollPickGuard.shouldSuppressSyntheticClick()) {
                e.preventDefault();
                return;
            }
            if (Date.now() - lastSearchableListPickAt < 450) {
                e.preventDefault();
                return;
            }
            handleSearchableListItemPick(e);
        });
        
        // Apply typeahead functionality
        applyTypeaheadToDropdown(searchInputId, selectId, listId);
        
        // Handle autofill events - validate when autofill occurs
        searchInput.addEventListener('change', function() {
            // Validate on change (autofill triggers change event)
            setTimeout(() => {
                validateSearchableDropdownInput(searchInputId, selectId);
            }, 100);
        });
        
        // Also validate when hidden select value changes (in case autofill sets it directly)
        select.addEventListener('change', function() {
            // If select has value but input doesn't match, validate
            if (this.value && this.value !== '') {
                setTimeout(() => {
                    validateSearchableDropdownInput(searchInputId, selectId);
                }, 100);
            }
        });
    }
    
    // Initialize address dropdown (non-searchable, shows all items)
    function initAddressDropdown(searchInputId, selectId, listId) {
        const searchInput = document.getElementById(searchInputId);
        const select = document.getElementById(selectId);
        const list = document.getElementById(listId);
        
        if (!searchInput || !select || !list) return;
        
        list.style.touchAction = 'manipulation';
        
        // Track if dropdown should stay open
        let keepDropdownOpen = false;
        let lastSavedAddressListPickAt = 0;
        const wrapper = searchInput.closest('.searchable-dropdown-wrapper') || searchInput.closest('.form-control');
        
        // Function to close dropdown
        function closeDropdown() {
            keepDropdownOpen = false;
            list.style.display = 'none';
        }
        
        // Function to open dropdown
        function openDropdown() {
            if (Date.now() < suppressAddressValidatorDropdownOpenUntil) {
                list.style.display = 'none';
                return;
            }
            keepDropdownOpen = true;
            // Close all other dropdowns first (for address dropdown, we don't have a listId, so close all)
            closeAllDropdowns(null);
            showAllDropdownItems(list);
            if (document.activeElement !== searchInput && !searchInput.disabled) {
                try {
                    searchInput.focus({ preventScroll: true });
                } catch (err) {
                    searchInput.focus();
                }
            }
        }
        
        let lastOpenedFromFocusAt = 0;
        
        // Show/hide dropdown on focus/blur
        searchInput.addEventListener('focus', function() {
            const listWasHidden = list.style.display === 'none' || list.style.display === '';
            openDropdown();
            if (listWasHidden) {
                lastOpenedFromFocusAt = Date.now();
            }
        });
        
        // Track touch position to distinguish taps from scrolls
        let touchStartY = null;
        let touchStartX = null;
        let isScrolling = false;
        let suppressMouseToggleUntil = 0;
        
        function handleOpenClosePointerDown(e) {
            if (e.pointerType === 'touch') {
                return;
            }
            e.stopPropagation();
            if (list.style.display === 'none' || list.style.display === '') {
                keepDropdownOpen = true;
                openDropdown();
            } else {
                closeDropdown();
            }
        }
        
        function handleOpenCloseMouseDown(e) {
            e.stopPropagation();
            if (Date.now() < suppressMouseToggleUntil) {
                return;
            }
            if (list.style.display === 'none' || list.style.display === '') {
                keepDropdownOpen = true;
                openDropdown();
            } else {
                closeDropdown();
            }
        }
        
        if (typeof PointerEvent !== 'undefined') {
            searchInput.addEventListener('pointerdown', handleOpenClosePointerDown);
        } else {
            searchInput.addEventListener('mousedown', handleOpenCloseMouseDown);
        }
        
        // Handle touch events with scroll detection for mobile
        searchInput.addEventListener('touchstart', function(e) {
            // Record touch start position
            const touch = e.touches[0];
            touchStartY = touch.clientY;
            touchStartX = touch.clientX;
            isScrolling = false;
        }, { passive: true });
        
        // Detect if user is scrolling (not tapping)
        searchInput.addEventListener('touchmove', function(e) {
            if (touchStartY !== null && touchStartX !== null) {
                const touch = e.touches[0];
                const deltaY = Math.abs(touch.clientY - touchStartY);
                const deltaX = Math.abs(touch.clientX - touchStartX);
                
                // If movement is significant, it's a scroll, not a tap
                if (deltaY > 5 || deltaX > 5) {
                    isScrolling = true;
                    // If dropdown is open and user is scrolling, close it
                    if (list.style.display === 'block' || list.style.display === '') {
                        closeDropdown();
                    }
                }
            }
        }, { passive: true });
        
        searchInput.addEventListener('touchend', function(e) {
            if (!isScrolling && touchStartY !== null) {
                e.stopPropagation();
                if (list.style.display === 'none' || list.style.display === '') {
                    keepDropdownOpen = true;
                    openDropdown();
                    if (typeof PointerEvent === 'undefined') {
                        suppressMouseToggleUntil = Date.now() + 500;
                    }
                } else if (Date.now() - lastOpenedFromFocusAt > 80) {
                    closeDropdown();
                }
            }
            touchStartY = null;
            touchStartX = null;
            isScrolling = false;
        }, { passive: false });
        
        // Also handle click for toggle behavior
        searchInput.addEventListener('click', function(e) {
            e.stopPropagation();
        });
        
        const savedListScrollPickGuard = createListScrollVsTapGuard();
        
        // Prevent blur when clicking on list
        list.addEventListener('mousedown', function(e) {
            e.preventDefault(); // Prevent input blur
            keepDropdownOpen = true;
        });
        list.addEventListener('touchstart', function(e) {
            keepDropdownOpen = true;
            savedListScrollPickGuard.touchStart(e);
        }, { passive: true });
        list.addEventListener('touchmove', function(e) {
            savedListScrollPickGuard.touchMove(e);
        }, { passive: true });
        list.addEventListener('touchcancel', function () {
            savedListScrollPickGuard.reset();
        }, { passive: true });
        
        searchInput.addEventListener('blur', function(e) {
            // Don't close if focus is moving to an element within the wrapper
            const relatedTarget = e.relatedTarget;
            if (relatedTarget && wrapper && wrapper.contains(relatedTarget)) {
                keepDropdownOpen = true;
                return;
            }
            
            // Delay hiding to allow click on list item
            setTimeout(() => {
                if (!keepDropdownOpen) {
                    closeDropdown();
                }
                keepDropdownOpen = false;
            }, 200);
        });
        
        function handleSavedAddressListPick(e) {
            const item = e.target.closest('.searchable-dropdown-item');
            if (!item) return;
            
            const value = item.getAttribute('data-value');
            const text = item.textContent.trim();
            
            if (!value || value === 'null' || value === 'undefined') {
                return;
            }
            
            lastSavedAddressListPickAt = Date.now();
            
            searchInput.value = text;
            
            let optionFound = false;
            const options = Array.from(select.options);
            for (let i = 0; i < options.length; i++) {
                if (options[i].value === value) {
                    select.selectedIndex = i;
                    select.value = value;
                    optionFound = true;
                    break;
                }
            }
            
            if (!optionFound) {
                for (let i = 0; i < options.length; i++) {
                    if (options[i].textContent.trim() === text) {
                        select.selectedIndex = i;
                        select.value = options[i].value;
                        optionFound = true;
                        break;
                    }
                }
            }
            
            if (selectId && (
                selectId === 'address-validator-province-select' ||
                selectId === 'address-validator-district-select' ||
                selectId === 'address-validator-subdistrict-select'
            )) {
                clearAllAddressFieldErrors();
            }
            
            select.dispatchEvent(new Event('change', { bubbles: true }));
            
            keepDropdownOpen = false;
            list.style.display = 'none';
            
            searchInput.focus();
            searchInput.blur();
        }
        
        list.addEventListener('touchend', function(e) {
            if (savedListScrollPickGuard.consumeIfScrollGesture()) return;
            const item = e.target.closest('.searchable-dropdown-item');
            if (!item) return;
            e.stopPropagation();
            e.preventDefault();
            handleSavedAddressListPick(e);
        }, { passive: false });
        
        list.addEventListener('click', function(e) {
            e.stopPropagation();
            if (savedListScrollPickGuard.shouldSuppressSyntheticClick()) {
                e.preventDefault();
                return;
            }
            if (Date.now() - lastSavedAddressListPickAt < 450) {
                e.preventDefault();
                return;
            }
            handleSavedAddressListPick(e);
        });
    }
    
    // Typeahead: Find first matching option in dropdown list
    function findMatchingDropdownItem(list, searchText) {
        if (!list || !searchText) return null;
        
        const searchLower = searchText.toLowerCase();
        const kids = list.children;
        for (let i = 0; i < kids.length; i++) {
            const row = kids[i];
            if (!row.classList || !row.classList.contains('searchable-dropdown-item')) continue;
            if (row.classList.contains('hidden')) continue;
            const ft = row.getAttribute('data-filter-text');
            if (ft !== null) {
                if (ft.includes(searchLower)) return row;
            } else if (row.textContent.toLowerCase().includes(searchLower)) {
                return row;
            }
        }
        
        return null;
    }
    
    // Typeahead: sync highlight to current input (called from rAF-batched input; not a second input listener)
    function updateTypeaheadHighlightForList(list, searchInput) {
        if (!list || !searchInput) return;
        const searchValue = String(searchInput.value).toLowerCase().trim();
        if (searchValue.length > 0) {
            const matchingItem = findMatchingDropdownItem(list, searchValue);
            if (matchingItem) {
                highlightDropdownItem(list, matchingItem);
            } else {
                const highlighted = list.querySelector('.searchable-dropdown-item.highlighted');
                if (highlighted) highlighted.classList.remove('highlighted');
            }
        } else {
            const highlighted = list.querySelector('.searchable-dropdown-item.highlighted');
            if (highlighted) highlighted.classList.remove('highlighted');
        }
    }
    
    // Typeahead: Highlight and scroll to matching item
    function highlightDropdownItem(list, item) {
        if (!list || !item) return;
        
        const prev = list.querySelector('.searchable-dropdown-item.highlighted');
        if (prev === item) return;
        
        if (prev) prev.classList.remove('highlighted');
        item.classList.add('highlighted');
        
        try {
            item.scrollIntoView({ block: 'nearest', behavior: 'auto' });
        } catch (e) {
            item.scrollIntoView(false);
        }
    }
    
    // Typeahead: Apply typeahead to searchable dropdown
    function applyTypeaheadToDropdown(searchInputId, selectId, listId) {
        const searchInput = document.getElementById(searchInputId);
        const select = selectId ? document.getElementById(selectId) : null;
        const list = document.getElementById(listId);
        
        if (!searchInput || !list) return;
        
        // Handle keydown for keyboard navigation
        searchInput.addEventListener('keydown', function(e) {
            // If dropdown is not visible, don't handle typeahead
            if (list.style.display === 'none') {
                return;
            }
            
            // Handle special keys
            if (e.key === 'Enter') {
                e.preventDefault();
                // Select the highlighted item
                const highlighted = list.querySelector('.searchable-dropdown-item.highlighted');
                if (highlighted) {
                    highlighted.click();
                } else {
                    // If no highlighted item, select first visible item
                    const firstItem = list.querySelector('.searchable-dropdown-item:not(.hidden)');
                    if (firstItem) {
                        firstItem.click();
                    }
                }
                return;
            }
            
            if (e.key === 'Escape') {
                list.style.display = 'none';
                return;
            }
            
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                // Navigate to next item
                const items = Array.from(list.querySelectorAll('.searchable-dropdown-item:not(.hidden)'));
                const currentIndex = items.findIndex(item => item.classList.contains('highlighted'));
                const nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
                if (items[nextIndex]) {
                    highlightDropdownItem(list, items[nextIndex]);
                } else if (items.length > 0 && currentIndex === -1) {
                    // If nothing is highlighted, highlight first item
                    highlightDropdownItem(list, items[0]);
                }
                return;
            }
            
            if (e.key === 'ArrowUp') {
                e.preventDefault();
                // Navigate to previous item
                const items = Array.from(list.querySelectorAll('.searchable-dropdown-item:not(.hidden)'));
                const currentIndex = items.findIndex(item => item.classList.contains('highlighted'));
                const prevIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
                if (items[prevIndex]) {
                    highlightDropdownItem(list, items[prevIndex]);
                } else if (items.length > 0 && currentIndex === -1) {
                    // If nothing is highlighted, highlight last item
                    highlightDropdownItem(list, items[items.length - 1]);
                }
                return;
            }
        });
    }
    
    // Populate province dropdown (Thailand)
    // Populate address dropdown list from select options
    function populateAddressDropdown() {
        const savedAddressSelect = document.getElementById('address-validator-saved-address');
        const addressList = document.getElementById('address-validator-saved-address-list');
        if (!savedAddressSelect || !addressList) return;
        
        // Clear existing list items
        addressList.innerHTML = '';
        
        // Populate list from select options (skip the first placeholder option)
        for (let i = 1; i < savedAddressSelect.options.length; i++) {
            const option = savedAddressSelect.options[i];
            const listItem = document.createElement('div');
            listItem.className = 'searchable-dropdown-item';
            listItem.textContent = option.textContent;
            listItem.setAttribute('data-value', option.value);
            addressList.appendChild(listItem);
        }
    }
    
    // Auto-select first address on page load
    function autoSelectFirstAddress() {
        const savedAddressSelect = document.getElementById('address-validator-saved-address');
        const savedAddressSearchInput = document.getElementById('address-validator-saved-address-search');
        if (!savedAddressSelect) return;
        
        // Check if there are any address options (more than just the placeholder)
        // The first option is the placeholder, so we need at least 2 options
        if (savedAddressSelect.options.length > 1) {
            // Select the first actual address option (index 1, since index 0 is the placeholder)
            const firstOption = savedAddressSelect.options[1];
            savedAddressSelect.selectedIndex = 1;
            
            // Update search input
            if (savedAddressSearchInput) {
                savedAddressSearchInput.value = firstOption.textContent;
            }
            
            // Trigger change event to prefill all fields
            savedAddressSelect.dispatchEvent(new Event('change', { bubbles: true }));
        }
    }
    
    
    // Setup form event handlers
    function setupFormHandlers() {
        const form = document.getElementById('address-validator-form');
        if (!form) return;
        
        // Prevent implicit HTML form submit (e.g. Enter in fields) from reloading the page
        form.addEventListener('submit', function (e) {
            e.preventDefault();
        });
        
        // Get all select elements
        const provinceSelect = document.getElementById('address-validator-province-select');
        const provinceSearchInput = document.getElementById('address-validator-province-search');
                const districtSelect = document.getElementById('address-validator-district-select');
                const districtSearchInput = document.getElementById('address-validator-district-search');
                                const subdistrictSelect = document.getElementById('address-validator-subdistrict-select');
                                const subdistrictSearchInput = document.getElementById('address-validator-subdistrict-search');
        
        // Handle saved address dropdown (if exists)
        const savedAddressSelect = document.getElementById('address-validator-saved-address');
        const savedAddressSearchInput = document.getElementById('address-validator-saved-address-search');
        if (savedAddressSelect) {
            savedAddressSelect.addEventListener('change', async function() {
                const selectedOption = this.options[this.selectedIndex];
                
                // Update search input with selected value
                if (savedAddressSearchInput && selectedOption) {
                    if (selectedOption.value) {
                        savedAddressSearchInput.value = selectedOption.textContent;
                    } else {
                        savedAddressSearchInput.value = '';
                    }
                }
                
                if (selectedOption && selectedOption.value) {
                    // Get address data from data attributes
                    // Thailand address mapping: address1 = Street, address2 = Sub-district, city = District, province = Province, zip = Postal Code
                    const address1 = selectedOption.value || '';
                    const firstName = selectedOption.getAttribute('data-first-name') || '';
                    const lastName = selectedOption.getAttribute('data-last-name') || '';
                    const subdistrict = selectedOption.getAttribute('data-address2') || ''; // address2 = Sub-district
                    const district = selectedOption.getAttribute('data-city') || ''; // city = District
                    const province = selectedOption.getAttribute('data-province') || '';
                    const zip = selectedOption.getAttribute('data-zip') || '';
                    
                    // Fill street address input field
                    const streetAddressInput = form.querySelector('input[name="address[address1]"]');
                    if (streetAddressInput && address1) {
                        streetAddressInput.value = address1;
                    }
                    
                    // Fill first name and last name if available
                    const firstNameInput = findInputByName('contact[first_name]');
                    const lastNameInput = findInputByName('contact[last_name]');
                    if (firstNameInput && firstName) {
                        firstNameInput.value = firstName;
                    }
                    if (lastNameInput && lastName) {
                        lastNameInput.value = lastName;
                    }
                    
                    // Use province for matching
                    const provinceToMatch = province;
                    
                    // Handle province, district, sub-district, and postal code matching with Sheety API
                    // Note: provinceSelect, districtSelect, subdistrictSelect, and their search inputs are already defined at function scope
                    const postalCodeInput = findPostalCodeInput();
                    
                    // Reset province, district, sub-district, and postal code first
                    // Note: Fields are always enabled now (unified dropdown system)
                    if (provinceSelect) {
                        provinceSelect.value = '';
                        provinceSelect.selectedIndex = 0;
                    }
                    if (provinceSearchInput) {
                        provinceSearchInput.value = '';
                    }
                    if (districtSelect) {
                        districtSelect.value = '';
                        districtSelect.selectedIndex = 0;
                    }
                    if (districtSearchInput) {
                        districtSearchInput.value = '';
                        // Ensure field is enabled (unified dropdowns are always enabled)
                        districtSearchInput.disabled = false;
                        districtSearchInput.removeAttribute('readonly');
                    }
                    if (subdistrictSelect) {
                        subdistrictSelect.value = '';
                        subdistrictSelect.selectedIndex = 0;
                    }
                    if (subdistrictSearchInput) {
                        subdistrictSearchInput.value = '';
                        // Ensure field is enabled (unified dropdowns are always enabled)
                        subdistrictSearchInput.disabled = false;
                        subdistrictSearchInput.removeAttribute('readonly');
                    }
                    if (postalCodeInput) {
                        postalCodeInput.value = '';
                    }
                    
                    // For Thailand addresses, find exact match using full hierarchy: Sub District | District | Province | Postcode
                    // All dropdowns now show the unified dataset, so we can match directly without dependencies
                    if (addressData.length > 0) {
                        // Wait for dropdowns to be populated
                        await new Promise(resolve => setTimeout(resolve, 100));
                        
                        // Find matching address data using available information
                        // Priority: Try to match by full hierarchy, then by partial matches
                        let matchingAddress = null;
                        
                        // Strategy 1: Try to find exact match using all available fields
                        if (subdistrict && district && province && zip) {
                            // Build composite key to find exact match
                            const compositeKey = [
                                String(subdistrict).trim(),
                                String(district).trim(),
                                String(province).trim(),
                                String(zip).trim()
                            ].join('|');
                            
                            // Try to find by composite key in addressDataMap (fast O(1) lookup)
                            matchingAddress = addressDataMap.get(compositeKey);
                            
                            // If not found in map, search in addressData (might be province name instead of ID)
                            if (!matchingAddress) {
                                matchingAddress = addressData.find(item => {
                                    const subdistrictMatch = String(item.subDistrictTh || '').trim() === String(subdistrict).trim() ||
                                                           String(item.subDistrictEn || '').trim().toLowerCase() === String(subdistrict).trim().toLowerCase();
                                    const districtMatch = String(item.districtTh || '').trim() === String(district).trim() ||
                                                        String(item.districtEn || '').trim().toLowerCase() === String(district).trim().toLowerCase();
                                    const provinceMatch = String(item.provinceId || '') === String(province) ||
                                                         String(item.provinceTh || '').trim() === String(province).trim() ||
                                                         String(item.provinceEn || '').trim().toLowerCase() === String(province).trim().toLowerCase();
                                    const postcodeMatch = String(item.postcode || '') === String(zip);
                                    
                                    return subdistrictMatch && districtMatch && provinceMatch && postcodeMatch;
                                });
                            }
                        }
                        
                        // Strategy 2: If no exact match, try matching by postal code (most reliable)
                        if (!matchingAddress && zip) {
                            matchingAddress = addressData.find(item => String(item.postcode || '') === String(zip));
                        }
                        
                        // Strategy 3: Try matching by province + district + subdistrict (without postcode)
                        if (!matchingAddress && subdistrict && district && province) {
                            matchingAddress = addressData.find(item => {
                                const subdistrictMatch = String(item.subDistrictTh || '').trim() === String(subdistrict).trim() ||
                                                       String(item.subDistrictEn || '').trim().toLowerCase() === String(subdistrict).trim().toLowerCase();
                                const districtMatch = String(item.districtTh || '').trim() === String(district).trim() ||
                                                    String(item.districtEn || '').trim().toLowerCase() === String(district).trim().toLowerCase();
                                const provinceMatch = String(item.provinceId || '') === String(province) ||
                                                   String(item.provinceTh || '').trim() === String(province).trim() ||
                                                   String(item.provinceEn || '').trim().toLowerCase() === String(province).trim().toLowerCase();
                                
                                return subdistrictMatch && districtMatch && provinceMatch;
                            });
                        }
                        
                        // If we found a matching address, fill all fields using unified dropdown system
                        if (matchingAddress) {
                            // Fill Sub District (first field in new order)
                            if (subdistrictSelect && subdistrictSearchInput) {
                                const subdistrictValue = matchingAddress.subDistrictTh || '';
                                const subdistrictDisplay = getDisplayName(matchingAddress, 'subDistrict');
                                
                                // Find matching option in unified dropdown
                                const subdistrictOption = Array.from(subdistrictSelect.options).find(opt => 
                                    opt.value === subdistrictValue && opt.value !== ''
                                );
                                
                                if (subdistrictOption) {
                                    subdistrictSelect.value = subdistrictValue;
                                    subdistrictSearchInput.value = subdistrictDisplay || subdistrictOption.textContent;
                                }
                            }
                            
                            // Fill District (second field)
                            if (districtSelect && districtSearchInput) {
                                const districtValue = matchingAddress.districtTh || '';
                                const districtDisplay = getDisplayName(matchingAddress, 'district');
                                
                                const districtOption = Array.from(districtSelect.options).find(opt => 
                                    opt.value === districtValue && opt.value !== ''
                                );
                                
                                if (districtOption) {
                                    districtSelect.value = districtValue;
                                    districtSearchInput.value = districtDisplay || districtOption.textContent;
                                }
                            }
                            
                            // Fill Province (third field)
                            if (provinceSelect && provinceSearchInput) {
                                const provinceValue = String(matchingAddress.provinceId || '');
                                const provinceDisplay = getDisplayName(matchingAddress, 'province');
                                
                                const provinceOption = Array.from(provinceSelect.options).find(opt => 
                                    opt.value === provinceValue && opt.value !== ''
                                );
                                
                                if (provinceOption) {
                                    provinceSelect.value = provinceValue;
                                    provinceSearchInput.value = provinceDisplay || provinceOption.textContent;
                                }
                            }
                            
                            // Fill Postcode (fourth field)
                            if (postalCodeInput) {
                                postalCodeInput.value = String(matchingAddress.postcode || zip || '');
                            }
                            
                            // Ensure all fields are enabled after auto-fill (unified dropdowns are always enabled)
                            if (subdistrictSearchInput) {
                                subdistrictSearchInput.disabled = false;
                                subdistrictSearchInput.removeAttribute('readonly');
                            }
                            if (districtSearchInput) {
                                districtSearchInput.disabled = false;
                                districtSearchInput.removeAttribute('readonly');
                            }
                            if (provinceSearchInput) {
                                provinceSearchInput.disabled = false;
                                provinceSearchInput.removeAttribute('readonly');
                            }
                            if (postalCodeInput) {
                                postalCodeInput.disabled = false;
                                postalCodeInput.removeAttribute('readonly');
                            }
                        } else if (zip && postalCodeInput) {
                            // Fallback: If no match found but we have postal code, just set it
                            postalCodeInput.value = zip;
                            
                            // Ensure fields are still enabled even if no match found
                            if (subdistrictSearchInput) {
                                subdistrictSearchInput.disabled = false;
                                subdistrictSearchInput.removeAttribute('readonly');
                            }
                            if (districtSearchInput) {
                                districtSearchInput.disabled = false;
                                districtSearchInput.removeAttribute('readonly');
                            }
                            if (provinceSearchInput) {
                                provinceSearchInput.disabled = false;
                                provinceSearchInput.removeAttribute('readonly');
                            }
                            if (postalCodeInput) {
                                postalCodeInput.disabled = false;
                                postalCodeInput.removeAttribute('readonly');
                            }
                        }
                    }
                    
                    clearAllErrors();
                }
            });
        }
        
        // All fields are now enabled by default (populated in loadAddressData)
        
        // Province change handler - no dependencies, just update the input field
        if (provinceSelect) {
            provinceSelect.addEventListener('change', function() {
                const selectedOption = this.options[this.selectedIndex];
                
                // Update search input with selected value
                if (provinceSearchInput && selectedOption && selectedOption.value) {
                    provinceSearchInput.value = selectedOption.textContent;
                }
                
                // Clear error when user selects a value
                clearFieldError('select[name="address[province]"]');
            });
        }
  
        // District change handler - no dependencies, just update the input field
        if (districtSelect) {
            districtSelect.addEventListener('change', function() {
                const selectedOption = this.options[this.selectedIndex];
                
                // Update search input with selected value
                if (districtSearchInput && selectedOption && selectedOption.value) {
                    districtSearchInput.value = selectedOption.textContent;
                }
                
                // Clear error when user selects a value
                clearFieldError('select[name="address[district]"]');
            });
        }
        
        // Sub-district change handler - no dependencies, just update the input field
        if (subdistrictSelect) {
            subdistrictSelect.addEventListener('change', function() {
                const selectedOption = this.options[this.selectedIndex];
                
                // Update search input with selected value
                if (subdistrictSearchInput && selectedOption && selectedOption.value) {
                    subdistrictSearchInput.value = selectedOption.textContent;
                }
                
                // Clear error when user selects a value
                clearFieldError('select[name="address[subdistrict]"]');
            });
        }
        
        // Postcode handler is now in initPostcodeDropdown() - no need for separate handler here
  
        // Real-time character limit validation for first name, last name, and address (no debounce)
        const firstNameInput = form.querySelector('input[name="contact[first_name]"]');
        const lastNameInput = form.querySelector('input[name="contact[last_name]"]');
        const streetAddressInput = form.querySelector('input[name="address[address1]"]');
        
        // Helper function to check if character is allowed in name (letters, marks, and spaces only)
        function isValidNameChar(char) {
            // Allow letters (any language), marks (combining characters like Thai vowels, accents), and spaces
            // \p{L} matches any letter from any language (Unicode property)
            // \p{M} matches any mark (combining characters like Thai vowels ั ิ ี, tone marks, diacritics)
            const letterMarkOrSpaceRegex = /^[\p{L}\p{M}\s]$/u;
            return letterMarkOrSpaceRegex.test(char);
        }
        
        // Helper function to filter name input (remove invalid characters)
        function filterNameInput(value) {
            // Remove numbers and special characters, keep letters, marks (Thai vowels, accents), and spaces
            return value.replace(/[^\p{L}\p{M}\s]/gu, '');
        }
        
        if (firstNameInput) {
            // Block invalid characters on keypress
            firstNameInput.addEventListener('keypress', function(e) {
                if (!isValidNameChar(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
                    e.preventDefault();
                }
            });
            
            // Filter out invalid characters on input (handles paste/autofill)
            firstNameInput.addEventListener('input', function() {
                const filtered = filterNameInput(this.value);
                if (this.value !== filtered) {
                    this.value = filtered;
                }
                
                const value = this.value.trim();
                if (value.length > 20) {
                    showFieldError('contact[first_name]', 'First name is too long (maximum is 20 characters)');
                } else if (value.length > 0) {
                    clearFieldError('contact[first_name]');
                }
            });
        }
        
        if (lastNameInput) {
            // Block invalid characters on keypress
            lastNameInput.addEventListener('keypress', function(e) {
                if (!isValidNameChar(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
                    e.preventDefault();
                }
            });
            
            // Filter out invalid characters on input (handles paste/autofill)
            lastNameInput.addEventListener('input', function() {
                const filtered = filterNameInput(this.value);
                if (this.value !== filtered) {
                    this.value = filtered;
                }
                
                const value = this.value.trim();
                if (value.length > 20) {
                    showFieldError('contact[last_name]', 'Last name is too long (maximum is 20 characters)');
                } else if (value.length > 0) {
                    clearFieldError('contact[last_name]');
                }
            });
        }
        
        if (streetAddressInput) {
            streetAddressInput.addEventListener('input', function() {
                const value = this.value.trim();
                if (value.length > 100) {
                    showFieldError('address[address1]', 'Address is too long (maximum is 100 characters)');
                } else if (value.length > 0) {
                    clearFieldError('address[address1]');
                }
            });
        }
        
        // Clear errors on input/select change
        const formInputs = form.querySelectorAll('input, select');
        formInputs.forEach(input => {
            input.addEventListener('input', function() {
                const fieldName = this.name || this.id;
                if (fieldName) {
                    // Skip character limit fields as they have their own handlers
                    if (fieldName === 'contact[first_name]' || fieldName === 'contact[last_name]' || fieldName === 'address[address1]') {
                        return;
                    }
                    
                    // Handle searchable dropdown inputs
                    if (this.classList.contains('searchable-dropdown-input')) {
                        let selectId = '';
                        if (this.id === 'address-validator-province-search') {
                            selectId = 'address-validator-province-select';
                        } else if (this.id === 'address-validator-district-search') {
                            selectId = 'address-validator-district-select';
                        } else if (this.id === 'address-validator-subdistrict-search') {
                            selectId = 'address-validator-subdistrict-select';
                        } else if (this.id === 'address-postal-code') {
                            // Postcode field uses different naming
                            clearFieldError('address[zip]');
                            return;
                        }
                        
                        if (selectId) {
                            clearFieldErrorForSearchableDropdown(this.id, selectId);
                            return;
                        }
                    }
                    
                    if (this.tagName === 'SELECT') {
                        clearFieldError(`select[name="${fieldName}"]`);
                    } else {
                        clearFieldError(fieldName);
                    }
                }
            });
            
            input.addEventListener('change', function() {
                const fieldName = this.name || this.id;
                if (fieldName) {
                    // Skip character limit fields as they have their own handlers
                    if (fieldName === 'contact[first_name]' || fieldName === 'contact[last_name]' || fieldName === 'address[address1]') {
                        return;
                    }
                    
                    // Handle searchable dropdown inputs
                    if (this.classList.contains('searchable-dropdown-input')) {
                        let selectId = '';
                        if (this.id === 'address-validator-province-search') {
                            selectId = 'address-validator-province-select';
                        } else if (this.id === 'address-validator-district-search') {
                            selectId = 'address-validator-district-select';
                        } else if (this.id === 'address-validator-subdistrict-search') {
                            selectId = 'address-validator-subdistrict-select';
                        } else if (this.id === 'address-postal-code') {
                            // Postcode field uses different naming
                            clearFieldError('address[zip]');
                            return;
                        }
                        
                        if (selectId) {
                            clearFieldErrorForSearchableDropdown(this.id, selectId);
                            return;
                        }
                    }
                    
                    if (this.tagName === 'SELECT') {
                        clearFieldError(`select[name="${fieldName}"]`);
                    } else {
                        clearFieldError(fieldName);
                    }
                }
            });
        });
        
        // Form submit handler — iOS WebKit often delivers touch in an order where click is flaky or
        // arrives after blur deferred work; flush on pointerdown + touchend (passive:false) + deduped click.
        const submitButton = document.querySelector('#address-validator-submit');
        if (submitButton) {
            function triggerAddressValidatorProceed(e) {
                if (e) {
                    e.preventDefault();
                }
                const now = Date.now();
                if (now - lastAddressValidatorProceedAt < 500) {
                    return;
                }
                lastAddressValidatorProceedAt = now;
                flushValidatorSearchableSyncFromInputs();
                validateAndSubmit();
            }
            submitButton.addEventListener(
                'touchend',
                function (e) {
                    if (!submitButton.contains(e.target)) {
                        return;
                    }
                    e.preventDefault();
                    triggerAddressValidatorProceed(e);
                },
                { passive: false }
            );
            submitButton.addEventListener('click', function (e) {
                e.preventDefault();
                if (Date.now() - lastAddressValidatorProceedAt < 500) {
                    return;
                }
                triggerAddressValidatorProceed(e);
            });
        }
    }
    
    // Clear error for a specific field
    function clearFieldError(fieldName) {
        const form = document.getElementById('address-validator-form');
        if (!form) return;
        
        // Find the field (input or select)
        let field, fieldControl, label;
        if (fieldName.includes('select')) {
            const match = fieldName.match(/select\[name="([^"]+)"/);
            if (match) {
                const name = match[1];
                // For searchable dropdowns, find the wrapper
                if (name === 'address[province]') {
                    field = document.getElementById('address-validator-province-search');
                    if (field) {
                        fieldControl = field.closest('.form-control') || field.closest('.searchable-dropdown-wrapper');
                        label = fieldControl ? fieldControl.querySelector('label:not(.floating-label)') : null;
                    }
                } else if (name === 'address[district]') {
                    field = document.getElementById('address-validator-district-search');
                    if (field) {
                        fieldControl = field.closest('.form-control') || field.closest('.searchable-dropdown-wrapper');
                        label = fieldControl ? fieldControl.querySelector('label:not(.floating-label)') : null;
                    }
                } else if (name === 'address[subdistrict]') {
                    field = document.getElementById('address-validator-subdistrict-search');
                    if (field) {
                        fieldControl = field.closest('.form-control') || field.closest('.searchable-dropdown-wrapper');
                        label = fieldControl ? fieldControl.querySelector('label:not(.floating-label)') : null;
                    }
                } else {
                    field = form.querySelector(`select[name="${name}"]`);
                    if (field) {
                        fieldControl = field.closest('.form-control') || field.closest('.searchable-dropdown-wrapper') || field.parentElement;
                        label = fieldControl ? fieldControl.querySelector('label:not(.floating-label)') : null;
                    }
                }
            }
        } else {
            field = form.querySelector(`input[name="${fieldName}"]`) || form.querySelector(`#${fieldName}`);
            if (field) {
                fieldControl = field.closest('.form-control') || field.parentElement;
                label = fieldControl ? fieldControl.querySelector('label:not(.floating-label)') : null;
            }
        }
        
        // Remove error message from label, wrapper, or fieldControl
        if (label) {
            // Check if label is in a wrapper
            const labelRow = label.closest('.field-label-row');
            if (labelRow) {
                const errorElement = labelRow.querySelector('.field-error');
                if (errorElement) {
                    errorElement.remove();
                    // If wrapper only has label now, unwrap it
                    if (labelRow.children.length === 1 && labelRow.firstChild === label) {
                        labelRow.parentNode.insertBefore(label, labelRow);
                        labelRow.remove();
                    }
                }
            } else {
                const errorElement = label.nextElementSibling;
                if (errorElement && errorElement.classList.contains('field-error')) {
                    errorElement.remove();
                }
            }
        }
        if (fieldControl) {
            const errorElement = fieldControl.querySelector('.field-error');
            if (errorElement) {
                errorElement.remove();
            }
        }
        
        // Remove error class
        if (fieldControl) {
            fieldControl.classList.remove('has-error');
        }
        if (field) {
            field.classList.remove('has-error');
        }
    }
    
    // Clear all error messages
    function clearAllErrors() {
        const form = document.getElementById('address-validator-form');
        if (!form) return;
        
        // Remove all error messages
        const errorMessages = form.querySelectorAll('.field-error');
        errorMessages.forEach(error => error.remove());
        
        // Remove error classes
        const errorControls = form.querySelectorAll('.form-control.has-error, .searchable-dropdown-wrapper.has-error');
        errorControls.forEach(control => control.classList.remove('has-error'));
        
        const errorInputs = form.querySelectorAll('input.has-error, select.has-error, .input.has-error, .select.has-error');
        errorInputs.forEach(input => input.classList.remove('has-error'));
    }
    
    // Show error message for a field
    function showFieldError(fieldName, message) {
        const form = document.getElementById('address-validator-form');
        if (!form) return null;
        
        let field, fieldControl, label;
        
        // Find the field (input or select)
        if (fieldName.includes('select[name=')) {
            // Extract the name from select[name="address[province]"]
            const match = fieldName.match(/select\[name="([^"]+)"/);
            if (match) {
                const name = match[1];
                // For searchable dropdowns, find the wrapper
                if (name === 'address[province]') {
                    field = document.getElementById('address-validator-province-search');
                    if (field) {
                        fieldControl = field.closest('.form-control') || field.closest('.searchable-dropdown-wrapper');
                        label = fieldControl ? fieldControl.querySelector('label:not(.floating-label)') : null;
                    }
                } else if (name === 'address[district]') {
                    field = document.getElementById('address-validator-district-search');
                    if (field) {
                        fieldControl = field.closest('.form-control') || field.closest('.searchable-dropdown-wrapper');
                        label = fieldControl ? fieldControl.querySelector('label:not(.floating-label)') : null;
                    }
                } else if (name === 'address[subdistrict]') {
                    field = document.getElementById('address-validator-subdistrict-search');
                    if (field) {
                        fieldControl = field.closest('.form-control') || field.closest('.searchable-dropdown-wrapper');
                        label = fieldControl ? fieldControl.querySelector('label:not(.floating-label)') : null;
                    }
                } else {
                    field = form.querySelector(`select[name="${name}"]`);
                    if (field) {
                        // Find parent container (could be .form-control or wrapper div)
                        fieldControl = field.closest('.form-control') || field.closest('.searchable-dropdown-wrapper') || field.parentElement;
                        // Find label - should be in form-control
                        label = fieldControl ? fieldControl.querySelector('label:not(.floating-label)') : null;
                    }
                }
            }
        } else {
            field = form.querySelector(`input[name="${fieldName}"]`) || form.querySelector(`#${fieldName}`);
            if (field) {
                // Find parent container - should be .form-control
                fieldControl = field.closest('.form-control') || field.parentElement;
                // Find label - should be in form-control (regular label, not floating)
                label = fieldControl ? fieldControl.querySelector('label:not(.floating-label)') : null;
            }
        }
        
        if (!field) return null;
        
        // Remove existing error if any
        if (fieldControl) {
            const existingError = fieldControl.querySelector('.field-error');
            if (existingError) {
                existingError.remove();
            }
        }
        if (label) {
            const existingError = label.querySelector('.field-error');
            if (existingError) {
                existingError.remove();
            }
        }
        
        // Add error class to field container
        if (fieldControl) {
            fieldControl.classList.add('has-error');
        }
        if (field) {
            field.classList.add('has-error');
        }
        
        // Create error message element
        const errorElement = document.createElement('span');
        errorElement.className = 'field-error';
        errorElement.textContent = message;
        
        // Insert error message beside the label (after label)
        if (label) {
            // Check if label is already in a label-row wrapper
            let labelRow = label.closest('.field-label-row');
            
            if (!labelRow) {
                // Create a wrapper div for label and error to be side by side
                labelRow = document.createElement('div');
                labelRow.className = 'field-label-row';
                
                // Insert wrapper before label
                const parent = label.parentNode;
                parent.insertBefore(labelRow, label);
                
                // Move label into wrapper
                labelRow.appendChild(label);
            }
            
            // Add error to the wrapper
            labelRow.appendChild(errorElement);
        } else if (fieldControl) {
            // If no label found, insert at the top of form-control
            fieldControl.insertBefore(errorElement, fieldControl.firstChild);
        } else {
            // Last resort: insert before field
            field.parentNode.insertBefore(errorElement, field);
        }
        
        return field;
    }
    
    /** Sync hidden hierarchy <select>s from visible inputs before submit validation (blur defers this 200ms). */
    function flushValidatorSearchableSyncFromInputs() {
        validateSearchableDropdownInput('address-validator-province-search', 'address-validator-province-select');
        validateSearchableDropdownInput('address-validator-district-search', 'address-validator-district-select');
        validateSearchableDropdownInput('address-validator-subdistrict-search', 'address-validator-subdistrict-select');
    }
    
    // Validate form
    function validateForm() {
      const form = document.getElementById('address-validator-form');
      if (!form) return false;
        
        // Clear previous errors
      clearAllErrors();
  
        // Mobile Safari / iOS: blur handlers defer sync + validate by 200ms so list picks still work.
        // A tap on "Proceed" can run validation before that timeout, leaving hidden <select> values stale
        // while the visible search inputs still show the chosen text — validateForm then fails and
        // focus() on the first error field re-opens a dropdown (looks like a double-tap bug).
        flushValidatorSearchableSyncFromInputs();
  
        // Get form values
        const firstNameInput = form.querySelector('input[name="contact[first_name]"]');
        const lastNameInput = form.querySelector('input[name="contact[last_name]"]');
        const emailInput = form.querySelector('input[name="contact[email]"]');
        const streetAddressInput = form.querySelector('input[name="address[address1]"]');
        const provinceSelect = document.getElementById('address-validator-province-select');
        const districtSelect = document.getElementById('address-validator-district-select');
        const subdistrictSelect = document.getElementById('address-validator-subdistrict-select');
        
        const firstName = firstNameInput ? firstNameInput.value.trim() : '';
        const lastName = lastNameInput ? lastNameInput.value.trim() : '';
        const email = emailInput ? emailInput.value.trim() : '';
        const streetAddress = streetAddressInput ? streetAddressInput.value.trim() : '';
        const province = provinceSelect ? provinceSelect.value : '';
        const district = districtSelect ? districtSelect.value : '';
        const subdistrict = subdistrictSelect ? subdistrictSelect.value : '';
        
        // Track errors and first error field for focusing
        let firstErrorField = null;
        let hasErrors = false;
        
        // Helper regex to check for invalid characters (numbers and special characters)
        // \p{L} = letters, \p{M} = marks (Thai vowels, accents, diacritics)
        const invalidNameCharsRegex = /[^\p{L}\p{M}\s]/u;
        
        // Validate first name
        if (!firstName) {
            const field = showFieldError('contact[first_name]', 'Please fill in this field');
            if (!firstErrorField) firstErrorField = field;
            hasErrors = true;
        } else if (firstName.length > 20) {
            const field = showFieldError('contact[first_name]', 'First name is too long (maximum is 20 characters)');
            if (!firstErrorField) firstErrorField = field;
            hasErrors = true;
        } else if (invalidNameCharsRegex.test(firstName)) {
            const field = showFieldError('contact[first_name]', 'First name cannot contain numbers or special characters');
            if (!firstErrorField) firstErrorField = field;
            hasErrors = true;
        }
        
        // Validate last name
        if (!lastName) {
            const field = showFieldError('contact[last_name]', 'Please fill in this field');
            if (!firstErrorField) firstErrorField = field;
            hasErrors = true;
        } else if (lastName.length > 20) {
            const field = showFieldError('contact[last_name]', 'Last name is too long (maximum is 20 characters)');
            if (!firstErrorField) firstErrorField = field;
            hasErrors = true;
        } else if (invalidNameCharsRegex.test(lastName)) {
            const field = showFieldError('contact[last_name]', 'Last name cannot contain numbers or special characters');
            if (!firstErrorField) firstErrorField = field;
            hasErrors = true;
        }
        
        // Validate email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!email) {
            const field = showFieldError('contact[email]', 'Please fill in this field');
            if (!firstErrorField) firstErrorField = field;
            hasErrors = true;
        } else if (!emailRegex.test(email)) {
            const field = showFieldError('contact[email]', 'Please enter a valid email address');
            if (!firstErrorField) firstErrorField = field;
            hasErrors = true;
        }
        
        // Validate street address
        if (!streetAddress) {
            const field = showFieldError('address[address1]', 'Please fill in this field');
            if (!firstErrorField) firstErrorField = field;
            hasErrors = true;
        } else if (streetAddress.length > 100) {
            const field = showFieldError('address[address1]', 'Address is too long (maximum is 100 characters)');
            if (!firstErrorField) firstErrorField = field;
            hasErrors = true;
        }
        
        // Validate province - check both select value and input value match
        const provinceSearchInput = document.getElementById('address-validator-province-search');
        if (!province) {
            const field = showFieldError('select[name="address[province]"]', 'Please select an item in the list');
            if (!firstErrorField) firstErrorField = field;
            hasErrors = true;
        } else {
            // Validate that select value exists in valid options (prevents autofill bypass)
            if (!validateSelectValueExists('address-validator-province-select')) {
                const field = showFieldError('select[name="address[province]"]', 'Please select an item from the list');
                if (!firstErrorField) firstErrorField = field;
                hasErrors = true;
            } else if (provinceSearchInput && provinceSearchInput.value.trim() !== '') {
                // Validate that input value matches the selected option (field-specific value only)
                const provinceOption = provinceSelect.options[provinceSelect.selectedIndex];
                if (provinceOption) {
                    // Get field-specific value from option (not full hierarchy, not other fields)
                    const optionFieldValue = getFieldDisplayValueFromOption(provinceOption, 'province');
                    if (optionFieldValue.toLowerCase() !== provinceSearchInput.value.trim().toLowerCase()) {
                        const field = showFieldError('select[name="address[province]"]', 'Please select an item from the list');
                        if (!firstErrorField) firstErrorField = field;
                        hasErrors = true;
                    }
                } else {
                    const field = showFieldError('select[name="address[province]"]', 'Please select an item from the list');
                    if (!firstErrorField) firstErrorField = field;
                    hasErrors = true;
                }
            }
        }
        
        // Validate district - check both select value and input value match
        const districtSearchInput = document.getElementById('address-validator-district-search');
        if (!district) {
            const field = showFieldError('select[name="address[district]"]', 'Please select an item in the list');
            if (!firstErrorField) firstErrorField = field;
            hasErrors = true;
        } else {
            // Validate that select value exists in valid options (prevents autofill bypass)
            if (!validateSelectValueExists('address-validator-district-select')) {
                const field = showFieldError('select[name="address[district]"]', 'Please select an item from the list');
                if (!firstErrorField) firstErrorField = field;
                hasErrors = true;
            } else if (districtSearchInput && districtSearchInput.value.trim() !== '') {
                // Validate that input value matches the selected option (field-specific value only)
                const districtOption = districtSelect.options[districtSelect.selectedIndex];
                if (districtOption) {
                    // Get field-specific value from option (not full hierarchy, not other fields)
                    const optionFieldValue = getFieldDisplayValueFromOption(districtOption, 'district');
                    if (optionFieldValue.toLowerCase() !== districtSearchInput.value.trim().toLowerCase()) {
                        const field = showFieldError('select[name="address[district]"]', 'Please select an item from the list');
                        if (!firstErrorField) firstErrorField = field;
                        hasErrors = true;
                    }
                } else {
                    const field = showFieldError('select[name="address[district]"]', 'Please select an item from the list');
                    if (!firstErrorField) firstErrorField = field;
                    hasErrors = true;
                }
            }
        }
        
        // Validate sub-district - check both select value and input value match
        const subdistrictSearchInput = document.getElementById('address-validator-subdistrict-search');
        if (!subdistrict) {
            const field = showFieldError('select[name="address[subdistrict]"]', 'Please select an item in the list');
            if (!firstErrorField) firstErrorField = field;
            hasErrors = true;
        } else {
            // Validate that select value exists in valid options (prevents autofill bypass)
            if (!validateSelectValueExists('address-validator-subdistrict-select')) {
                const field = showFieldError('select[name="address[subdistrict]"]', 'Please select an item from the list');
                if (!firstErrorField) firstErrorField = field;
                hasErrors = true;
            } else if (subdistrictSearchInput && subdistrictSearchInput.value.trim() !== '') {
                // Validate that input value matches the selected option (field-specific value only)
                const subdistrictOption = subdistrictSelect.options[subdistrictSelect.selectedIndex];
                if (subdistrictOption) {
                    // Get field-specific value from option (not full hierarchy, not other fields)
                    const optionFieldValue = getFieldDisplayValueFromOption(subdistrictOption, 'subdistrict');
                    if (optionFieldValue.toLowerCase() !== subdistrictSearchInput.value.trim().toLowerCase()) {
                        const field = showFieldError('select[name="address[subdistrict]"]', 'Please select an item from the list');
                        if (!firstErrorField) firstErrorField = field;
                        hasErrors = true;
                    }
                } else {
                    const field = showFieldError('select[name="address[subdistrict]"]', 'Please select an item from the list');
                    if (!firstErrorField) firstErrorField = field;
                    hasErrors = true;
                }
            }
        }
        
        // Validate postal code
        const postalCodeInput = findPostalCodeInput();
        const postalCode = postalCodeInput ? postalCodeInput.value.trim() : '';
        if (!postalCode) {
            const field = showFieldError('address[zip]', 'Please fill in this field');
            if (!firstErrorField) firstErrorField = field;
            hasErrors = true;
        } else if (postalCode.length !== 5) {
            const field = showFieldError('address[zip]', 'Postal code must be 5 digits');
            if (!firstErrorField) firstErrorField = field;
            hasErrors = true;
        } else if (!/^[0-9]+$/.test(postalCode)) {
            const field = showFieldError('address[zip]', 'Postal code must contain only numbers');
            if (!firstErrorField) firstErrorField = field;
            hasErrors = true;
        } else {
            // Validate that postal code exists using fast Set lookup (O(1) instead of O(n))
            if (!postcodeSet.has(postalCode)) {
                const field = showFieldError('address[zip]', 'Invalid postal code');
                if (!firstErrorField) firstErrorField = field;
                hasErrors = true;
            }
        }
        
        if (hasErrors) {
            // Focus on first error field and scroll to it
            if (firstErrorField) {
                suppressAddressValidatorDropdownOpenUntil = Date.now() + 500;
                try {
                    firstErrorField.focus({ preventScroll: true });
                } catch (err) {
                    firstErrorField.focus();
                }
                firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return false;
        }
        
        return true;
    }
    
    // Validate and submit form
    async function validateAndSubmit() {
        if (!validateForm()) {
            return;
        }
  
        // Get form values
      const form = document.getElementById('address-validator-form');
        const firstName = form.querySelector('input[name="contact[first_name]"]').value.trim();
        const lastName = form.querySelector('input[name="contact[last_name]"]').value.trim();
        const email = form.querySelector('input[name="contact[email]"]').value.trim();
        const streetAddressInput = form.querySelector('input[name="address[address1]"]');
        const streetAddress = streetAddressInput ? streetAddressInput.value.trim() : '';
        // Handle both visible select and hidden input for country field
        const countrySelect = form.querySelector('select[name="address[country]"]');
        const countryInput = form.querySelector('input[name="address[country]"]');
        const country = countrySelect ? countrySelect.value : (countryInput ? countryInput.value : 'Thailand');
        const provinceSelect = document.getElementById('address-validator-province-select');
        const districtSelect = document.getElementById('address-validator-district-select');
        const subdistrictSelect = document.getElementById('address-validator-subdistrict-select');
        const postalCodeInput = form.querySelector('#address-postal-code');
        
        // Get names based on current language (TH or EN)
        const provinceOption = provinceSelect && provinceSelect.selectedIndex > 0 ? provinceSelect.options[provinceSelect.selectedIndex] : null;
        const districtOption = districtSelect && districtSelect.selectedIndex > 0 ? districtSelect.options[districtSelect.selectedIndex] : null;
        const subdistrictOption = subdistrictSelect && subdistrictSelect.selectedIndex > 0 ? subdistrictSelect.options[subdistrictSelect.selectedIndex] : null;
        
        // Use data-th-name for Thai, data-en-name for English
        const langAttr = currentLanguage === 'th' ? 'data-th-name' : 'data-en-name';
        const province = provinceOption ? (provinceOption.getAttribute(langAttr) || provinceOption.text) : '';
        const district = districtOption ? (districtOption.getAttribute(langAttr) || districtOption.text) : '';
        const subdistrict = subdistrictOption ? (subdistrictOption.getAttribute(langAttr) || subdistrictOption.text) : '';
        const zip = postalCodeInput ? postalCodeInput.value.trim() : '';
        
        // Cart permalink: always send English province (Shopify matches data-en-name / TH-XX alternates better than Thai script)
        const provinceForCheckout = provinceOption
            ? ((provinceOption.getAttribute('data-en-name') || '').trim() || province)
            : '';
        
        // Show loading state
      const submitButton = document.querySelector('#address-validator-submit');
        if (submitButton) {
            submitButton.disabled = true;
            submitButton.textContent = 'Processing...';
        }
        
        try {
            // Fetch current cart to get cart items for permalink
            const cartResponse = await fetch(`${Shopify.routes.root}cart.js`);
            const cart = await cartResponse.json();
            
            // Build cart permalink from cart items
            // Format: /cart/variant_id:quantity,variant_id:quantity
            const cartItems = cart.items.map(item => `${item.variant_id}:${item.quantity}`).join(',');
        const baseCartUrl = `${Shopify.routes.root}cart/${cartItems}`;
  
            // Build checkout query parameters
            const checkoutParams = new URLSearchParams();
            checkoutParams.append('checkout[email]', email);
            checkoutParams.append('checkout[shipping_address][first_name]', firstName);
            checkoutParams.append('checkout[shipping_address][last_name]', lastName);
            checkoutParams.append('checkout[shipping_address][address1]', streetAddress);
            // For Thailand: address2 = subdistrict, city = district
            checkoutParams.append('checkout[shipping_address][address2]', subdistrict);
            checkoutParams.append('checkout[shipping_address][city]', district);
            checkoutParams.append('checkout[shipping_address][province]', provinceForCheckout);
            checkoutParams.append('checkout[shipping_address][country]', country);
            checkoutParams.append('checkout[shipping_address][zip]', zip);
            
            // Construct full checkout URL
            const checkoutUrl = `${baseCartUrl}?${checkoutParams.toString()}`;
            
            // Log checkout link
            console.log('Checkout URL:', checkoutUrl);
            
            // Re-enable button before redirect (in case redirect fails or is blocked)
            if (submitButton) {
                submitButton.disabled = false;
                submitButton.textContent = 'Proceed to Payment';
            }
            
            // Redirect to checkout with prefilled data
            window.location.href = checkoutUrl;
        } catch (error) {
            alert('There was an error generating your checkout link. Please try again.');
            if (submitButton) {
                submitButton.disabled = false;
                submitButton.textContent = 'Proceed to Payment';
            }
        }
    }
    
    // Initialize when DOM is ready
    function initialize() {
        // Initialize address validator form (for validator page)
      initAddressValidatorForm();
    }
  
    // Check if DOM is already loaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initialize);
    } else {
        // DOM is already loaded
      initialize();
    }
  
  })();

